import React, { useEffect, useState, useCallback } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { useParams, useNavigate } from "react-router-dom";
import { logger } from "@/Services/Logger/Index";
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  LinkOutlined,
  GlobalOutlined,
  FileTextOutlined,
  DownloadOutlined,
  FileOutlined,
  AppstoreAddOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Layout,
  Skeleton,
  Typography,
  message,
  FloatButton,
  Modal,
  Form,
  Input,
  DatePicker,
  Tag,
  Tooltip,
  Empty,
  Checkbox,
  Select,
  Segmented,
} from "antd";
import { useAuthenticatedFetch } from "@/Api/Axios";
import { sortResumeForDisplay, sortExperienceByDate, sortEducationByDate } from "@/Utils/DateSortingUtils";
import { formatDateToMMYYYY, formatDateRange } from "@/Utils/Resume/DateFormattingUtils";
import {
  formatDescriptionBullets,
  formatAchievementBullets
} from "@/Utils/Resume/TextFormattingComponents.jsx";
import {
  getSectionHeaderStyles,
  getSectionContainerStyles,
  getItemContainerStyles,
  getFlexContainerStyles,
  getButtonStyles,
  SPACING,
  FONT_SIZES,
  COLORS
} from "@/Utils/Resume/TextFormattingUtils.js";
import AppLayout from "@/Components/Common/AppLayout";
import { useAuth } from "@/Context/AuthContext";
import { useCredits } from "@/Context/CreditContext";
import { useDeleteConfirmation } from "@/Components/Common/DeleteConfirmation";
import PublicResumeModal from "@/Components/Common/PublicResumeModal";
import AddSectionModal from "@/Components/Common/AddSectionModal";
import dayjs from 'dayjs';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Simple tooltip configuration for AI analysis
const useTooltipPlacement = () => {
  // Fixed placement - always show AI analysis on left side
  const getDynamicPlacement = useCallback(() => {
    return 'left';
  }, []);

  // Fixed overlay style
  const getTooltipOverlayStyle = useCallback(() => {
    return { 
      maxWidth: '400px'
    };
  }, []);

  return { getDynamicPlacement, getTooltipOverlayStyle };
};

// Global Roboto font registration for the component
const loadRobotoFonts = () => {
  if (typeof document !== 'undefined') {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400;1,500;1,700&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
};

// Load Roboto fonts
loadRobotoFonts();

// Drag handle component - up/down arrows in a square box for reordering
const DragHandle = ({ dragHandleProps }) => (
  <div
    {...dragHandleProps}
    style={{
      cursor: "grab",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: "26px",
      height: "26px",
      border: "1px solid #d9d9d9",
      backgroundColor: "#fff",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      borderRadius: "6px",
      transition: "all 0.2s",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = "#1890ff";
      e.currentTarget.style.color = "#1890ff";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = "#d9d9d9";
      e.currentTarget.style.color = "inherit";
    }}
    title="Drag to reorder"
  >
    <SwapOutlined style={{ fontSize: "14px", transform: "rotate(90deg)" }} />
  </div>
);

// Helper function to format description with proper bullet points - now using TextFormattingUtils
// This function is maintained for backward compatibility but now uses the centralized utility
const formatDescriptionBulletsLocal = (description) => {
  return formatDescriptionBullets(description, {
    fontSize: '13px',
    color: '#333',
    lineHeight: '1.5',
    bulletMargin: '6px',
    bulletSpacing: '8px',
    bulletTextStyles: {
      textAlign: 'justify'
    }
  });
};

// Format achievements specifically with bullet points - now using TextFormattingUtils
// This function is maintained for backward compatibility but now uses the centralized utility
const formatAchievementBulletsLocal = (description) => {
  return formatAchievementBullets(description, {
    fontSize: '13px',
    color: '#333',
    lineHeight: '1.5',
    bulletMargin: '6px',
    bulletSpacing: '8px',
    bulletColor: '#1890ff', // Blue bullets for achievements
    bulletTextStyles: {
      textAlign: 'justify'
    }
  });
};

// Helper function to get score color
const getScoreColor = (score) => {
  if (score >= 80) return '#52c41a'; // Green
  if (score >= 60) return '#faad14'; // Orange
  return '#ff4d4f'; // Red
};


// Helper function to calculate section score from AI analysis - ONLY for summary, experience, projects
const calculateSectionScore = (aiAnalysis) => {
  if (!aiAnalysis) return null;

  // If score is directly provided, use it (this is what our backend sets)
  if (aiAnalysis.score !== undefined && aiAnalysis.score !== null) {
    return aiAnalysis.score;
  }

  // Intelligent scoring based on AI analysis quality
  let score = 60; // Lower base score for more realistic assessment

  // Analyze whatsGood content (0-25 points)
  if (aiAnalysis.whatsGood) {
    const goodPoints = aiAnalysis.whatsGood.split(/[\u2022\n,]/).filter(point => point.trim().length > 10);
    score += Math.min(goodPoints.length * 8, 25); // Up to 25 points for good aspects
  }

  // Analyze whatToImprove content (impact on score)
  if (aiAnalysis.whatToImprove) {
    const improvementPoints = aiAnalysis.whatToImprove.split(/[\u2022\n,]/).filter(point => point.trim().length > 10);
    const penalty = Math.min(improvementPoints.length * 3, 15); // Reduce score based on issues found
    score -= penalty;
  }

  // Analyze aiSuggestions content (0-15 points for actionability)
  if (aiAnalysis.aiSuggestions) {
    const suggestionPoints = aiAnalysis.aiSuggestions.split(/[\u2022\n,]/).filter(point => point.trim().length > 10);
    score += Math.min(suggestionPoints.length * 5, 15); // Up to 15 points for suggestions
  }

  // Ensure score is within realistic bounds
  return Math.min(Math.max(score, 40), 100); // Min 40%, Max 100%
};

// Helper function to format AI analysis text with proper line breaks
const formatAnalysisText = (text) => {
  if (!text) return null;
  
  // Split by bullet points, newlines, or numbered points
  const points = text.split(/[\u2022\n]|\d+\./).filter(point => point.trim().length > 0);
  
  // If we have multiple points, format them as separate lines with proper alignment
  if (points.length > 1) {
    return points.map((point, index) => (
      <div key={index} style={{ 
        marginBottom: '3px', 
        display: 'flex', 
        alignItems: 'flex-start',
        textIndent: '0px'
      }}>
        <span style={{ 
          marginRight: '6px', 
          fontSize: '11px',
          lineHeight: '1.3',
          minWidth: '8px',
          flexShrink: 0
        }}>•</span>
        <span style={{ 
          flex: 1,
          lineHeight: '1.3'
        }}>{point.trim()}</span>
      </div>
    ));
  }
  
  // If single point or no bullet structure, return as is
  return <div style={{ lineHeight: '1.3' }}>{text}</div>;
};

// Editable Section Title Component - allows users to rename section headers
const EditableSectionTitle = ({ sectionKey, title, onTitleChange, style = {} }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = React.useRef(null);

  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== title) {
      onTitleChange(sectionKey, trimmedValue);
    } else {
      setEditValue(title); // Reset if empty or unchanged
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(title);
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={handleKeyDown}
        style={{
          fontSize: '15px',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          padding: '0 4px',
          height: '24px',
          width: 'auto',
          minWidth: '100px',
          maxWidth: '300px',
          border: '1px solid #1890ff',
          borderRadius: '4px',
          ...style
        }}
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      style={{
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        ...style
      }}
      title="Click to edit section title"
    >
      {title.toUpperCase()}
      <EditOutlined style={{ fontSize: '12px', color: '#999', opacity: 0.7 }} />
    </span>
  );
};

// Generic AI Analysis Tooltip Component - ONLY for summary, experience, projects, skills
const AIAnalysisTooltip = ({ analysisData, sectionName, itemIndex, content, description }) => {
  // Only show AI Analysis for allowed sections
  const allowedSections = ['summary', 'experience', 'projects', 'skills'];
  if (!allowedSections.includes(sectionName)) {
    return (
      <div style={{ padding: '6px 10px', maxWidth: '240px' }}>
        <div style={{ fontSize: '12px', color: '#666' }}>
          💡 AI Analysis not available for this section
        </div>
      </div>
    );
  }

  // Check if content is empty to show enhance with AI message instead of analysis
  const isEmpty = (sectionName === 'summary' && (!content || !content.trim())) ||
                  ((sectionName === 'experience' || sectionName === 'projects') && (!description || !description.trim()));

  if (!analysisData || isEmpty) {
    return (
      <div style={{ padding: '6px 10px', maxWidth: '240px' }}>
        <div style={{ fontSize: '12px', color: '#666' }}>
          💡 Use <strong>"Enhance with AI ✨"</strong> to get AI analysis
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: '280px', 
      padding: '8px 10px',
      fontSize: '11px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: '1.4'
    }}>
      {/* Compact Header with Score */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px',
        paddingBottom: '6px',
        borderBottom: '1px solid #e8e8e8'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px'
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#1890ff',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            🚀 AI Analysis{itemIndex !== undefined ? ` #${itemIndex + 1}` : ''}
          </div>
          <div style={{
            fontSize: '10px',
            color: '#595959',
            fontWeight: '600'
          }}>
            Click "Enhance with AI" to improve
          </div>
        </div>
        {(analysisData.score !== undefined || calculateSectionScore(analysisData)) && (
          <div style={{
            fontSize: '11px',
            fontWeight: '700',
            color: getScoreColor(analysisData.score || calculateSectionScore(analysisData)),
            backgroundColor: '#f8f9fa',
            padding: '2px 8px',
            borderRadius: '12px',
            border: '1px solid #e6e8eb'
          }}>
            {analysisData.score || calculateSectionScore(analysisData)}%
          </div>
        )}
      </div>

      {/* Compact Analysis Grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* What's Good - Minimal */}
        {analysisData.whatsGood && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3px'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#52c41a',
              display: 'flex',
              alignItems: 'center',
              gap: '3px'
            }}>
              ⭐ What's Good
            </div>
            <div style={{
              fontSize: '11px',
              lineHeight: '1.3',
              color: '#2c2c2c',
              backgroundColor: '#f6ffed',
              padding: '4px 6px',
              borderRadius: '4px',
              borderLeft: '3px solid #52c41a'
            }}>
              {formatAnalysisText(analysisData.whatsGood)}
            </div>
          </div>
        )}

        {/* What to Improve - Minimal */}
        {analysisData.whatToImprove && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3px'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#faad14',
              display: 'flex',
              alignItems: 'center',
              gap: '3px'
            }}>
              📌 Improvements
            </div>
            <div style={{
              fontSize: '11px',
              lineHeight: '1.3',
              color: '#2c2c2c',
              backgroundColor: '#fffbe6',
              padding: '4px 6px',
              borderRadius: '4px',
              borderLeft: '3px solid #faad14'
            }}>
              {formatAnalysisText(analysisData.whatToImprove)}
            </div>
          </div>
        )}

        {/* AI Suggestions - Minimal */}
        {analysisData.aiSuggestions && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '3px'
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: '#1890ff',
              display: 'flex',
              alignItems: 'center',
              gap: '3px'
            }}>
              💫 AI Tips
            </div>
            <div style={{
              fontSize: '11px',
              lineHeight: '1.3',
              color: '#2c2c2c',
              backgroundColor: '#f0f5ff',
              padding: '4px 6px',
              borderRadius: '4px',
              borderLeft: '3px solid #1890ff'
            }}>
              {formatAnalysisText(analysisData.aiSuggestions)}
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

// Inline Cover Letter Form Component
const InlineCoverLetterForm = ({ resumeData, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(resumeData?.coverLetter?.generatedContent || '');
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [coverLetterData, setCoverLetterData] = useState(resumeData?.coverLetter || null);
  const fetch = useAuthenticatedFetch();

  // Use local state to control view instead of prop
  const hasGeneratedCoverLetter = !!generatedContent;

  useEffect(() => {
    // Update local state when resumeData changes
    if (resumeData?.coverLetter) {
      form.setFieldsValue({
        targetRole: resumeData.coverLetter.targetRole,
        companyName: resumeData.coverLetter.companyName,
        jobDescription: resumeData.coverLetter.jobDescription,
      });
      setGeneratedContent(resumeData.coverLetter.generatedContent || '');
      setCoverLetterData(resumeData.coverLetter);
    } else {
      // Pre-fill targetRole from resume context if available
      const context = resumeData?.parsedData?.context;
      if (context?.targetRole) {
        form.setFieldsValue({ targetRole: context.targetRole });
      }
    }
  }, [resumeData, form]);

  const handleGenerate = async (values) => {
    try {
      setLoading(true);
      const response = await fetch.post('/generate-cover-letter', {
        resumeId: resumeData._id,
        ...values,
      });

      if (response.success && response.data) {
        setGeneratedContent(response.data.generatedContent);
        setCoverLetterData(response.data.resume?.coverLetter || {
          header: resumeData?.parsedData?.personalInfo || {},
          generatedContent: response.data.generatedContent,
        });
        message.success('Cover letter generated successfully!');
        if (onSuccess) onSuccess(response.data.resume);
      } else {
        message.error(response.message || 'Failed to generate cover letter');
      }
    } catch (error) {
      console.error('Error generating cover letter:', error);
      message.error('Failed to generate cover letter. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const response = await fetch.put('/update-cover-letter', {
        resumeId: resumeData._id,
        ...values,
        generatedContent,
      });

      if (response.success) {
        setIsEditingContent(false);
        message.success('Cover letter updated successfully!');
        if (onSuccess) onSuccess(response.data);
      }
    } catch (error) {
      message.error('Failed to update cover letter');
    } finally {
      setLoading(false);
    }
  };

  // Show generation form if no cover letter exists yet
  if (!hasGeneratedCoverLetter) {
    return (
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '24px',
      }}>
        <Card
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FileTextOutlined style={{ color: '#1890ff' }} />
              <span>Generate Cover Letter</span>
            </div>
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleGenerate}
          >
            <Form.Item
              label="Target Role"
              name="targetRole"
              rules={[{ required: true, message: 'Please enter target role' }]}
            >
              <Input placeholder="e.g., Senior Software Engineer" size="large" />
            </Form.Item>

            <Form.Item
              label="Company Name"
              name="companyName"
              rules={[{ required: true, message: 'Please enter company name' }]}
            >
              <Input placeholder="e.g., Google" size="large" />
            </Form.Item>

            <Form.Item
              label="Job Description"
              name="jobDescription"
              rules={[{ required: true, message: 'Please enter job description' }]}
            >
              <TextArea
                rows={8}
                placeholder="Paste the job description here..."
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                icon={<ThunderboltOutlined />}
              >
                Generate Cover Letter
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  // Show cover letter preview (like resume preview) with edit option
  return (
    <div style={{
      maxWidth: '800px',
      margin: '-16px auto 24px auto',
      padding: '0 16px',
    }}>
      {/* Cover Letter Preview Container (like resume) */}
      <div
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid #e1e4e8",
          borderRadius: "12px",
          padding: "40px 50px",
          fontFamily: "Roboto, sans-serif",
          fontSize: "14px",
          lineHeight: "1.8",
          boxShadow: "0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.12)",
          minHeight: "800px",
          position: "relative",
        }}
      >
        {/* Edit Button - Inside cover letter at top right */}
        <div style={{ position: 'absolute', top: '16px', right: '16px', zIndex: 10 }}>
          {!isEditingContent ? (
            <Button
              icon={<EditOutlined />}
              onClick={() => setIsEditingContent(true)}
              size="middle"
            >
              Edit Cover Letter
            </Button>
          ) : (
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleUpdate}
                loading={loading}
              >
                Save Changes
              </Button>
              <Button
                onClick={() => {
                  setGeneratedContent(resumeData?.coverLetter?.generatedContent || '');
                  setIsEditingContent(false);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
        </div>

        {/* Cover Letter Header */}
        <div style={{ marginBottom: '24px', borderBottom: '1px solid #e1e4e8', paddingBottom: '16px' }}>
          <div style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#1a1a1a',
            marginBottom: '8px'
          }}>
            {coverLetterData?.header?.name || '[Name]'}
          </div>
          <div style={{ fontSize: '13px', color: '#555555', marginBottom: '4px' }}>
            {coverLetterData?.header?.email || '[Email]'} | {coverLetterData?.header?.phone || '[Phone]'}
          </div>
          <div style={{ fontSize: '13px', color: '#555555' }}>
            {coverLetterData?.header?.location || '[Location]'}
          </div>
        </div>

        {/* Cover Letter Content */}
        {isEditingContent ? (
          <TextArea
            value={generatedContent}
            onChange={(e) => setGeneratedContent(e.target.value)}
            rows={30}
            style={{
              fontFamily: 'Roboto, sans-serif',
              fontSize: '14px',
              lineHeight: '1.8',
              border: 'none',
              resize: 'none',
              width: '100%',
            }}
          />
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>
            {generatedContent}
          </div>
        )}
      </div>
    </div>
  );
};

// Resume Preview Panel with Inline Controls
const ResumePreviewPanelWithControls = ({
  resumeData,
  setResumeData,
  onPreview,
  onEditPersonalInfo,
  onEditSummary,
  onAIEnhanceSummary,
  getDynamicPlacement,
  getTooltipOverlayStyle,
  onAddExperience,
  onEditExperience,
  onDeleteExperience,
  onAIEnhanceExperience,
  onAddEducation,
  onEditEducation,
  onDeleteEducation,
  onAIEnhanceEducation,
  onEditSkills,
  onAddProject,
  onEditProject,
  onDeleteProject,
  onAIEnhanceProject,
  onAddCertification,
  onEditCertification,
  onDeleteCertification,
  onEditAchievements,
  onEditLanguages,
  onEditAwards,
  // New Section props
  onEditVolunteering,
  onEditInterests,
  onEditIndustryExpertise,
  onEditPublications,
  onEditReferences,
  // Delete Section prop (for new sections)
  onDeleteSection,
  // Custom Section props
  onEditCustomSection,
  onDeleteCustomSection,
  // Add Section props
  onAddSection,
  visibleSections,
  // Reorder items within sections (drag and drop)
  onReorderItems,
  // Inline AI Enhancement props
  activeInlineInput,
  setActiveInlineInput,
  inlinePrompt,
  setInlinePrompt,
  enhancingSection,
  handleInlineEnhance,
  // Public Resume props
  isPublic,
  onOpenPublicModal,
  // Edit Mode (Resume/Cover Letter)
  editMode,
  setEditMode,
  // Section Titles props
  sectionTitles,
  onSectionTitleChange,
  // Credit state for UI disabling
  canManualEdit = true,
  canAddSection = true,
  creditBalance = 0,
  manualEditCost = 1,
  addSectionCost = 1,
}) => {
  // Only check for parsedData when in resume mode
  if (editMode === 'resume' && !resumeData?.parsedData) {
    return (
      <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <div
            style={{
              padding: "24px",
              textAlign: "center",
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            <Title level={4} style={{ margin: 0, marginBottom: SPACING.SECTION_MARGIN_BOTTOM }}>
              📄 Resume Preview
            </Title>
            <Empty
              description="Loading resume preview..."
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            />
          </div>
        </Card>
      </div>
    );
  }

  const data = editMode === 'resume' && resumeData?.parsedData ? sortResumeForDisplay(resumeData.parsedData) : null;

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "#ffffff",
          border: "none",
        }}
      >
        {/* Header with Toggle and Download button */}
        <div
          style={{
            padding: "5px 16px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "12px",
          }}
        >
          {/* Resume / Cover Letter Toggle */}
          <Segmented
            options={[
              {
                label: 'Resume',
                value: 'resume',
                icon: <FileOutlined />
              },
              {
                label: 'Cover Letter',
                value: 'coverLetter',
                icon: <FileTextOutlined />
              }
            ]}
            value={editMode}
            onChange={setEditMode}
            size="middle"
          />

          {/* Public Resume Settings Button */}
          <Button
            icon={<GlobalOutlined />}
            onClick={onOpenPublicModal}
            size="small"
            style={{
              borderRadius: "6px",
              fontSize: "12px",
              height: "28px",
              borderColor: isPublic ? "#52c41a" : "#d9d9d9",
              color: isPublic ? "#52c41a" : undefined
            }}
          >
            {isPublic ? 'Public Settings' : 'Make Public'}
          </Button>

          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={onPreview}
            size="small"
            style={{
              borderRadius: "6px",
              fontSize: "12px",
              height: "28px",
            }}
          >
            Download Resume
          </Button>
        </div>

        {/* Conditional Content Based on Edit Mode */}
        {editMode === 'resume' ? (
          /* Scrollable Resume Content */
          <div
            style={{
              flex: 1,
              overflowY: "scroll",
              overflowX: "hidden",
              padding: "0px 16px 16px 16px",
              height: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: "#d1d5db #f9fafb",
              WebkitOverflowScrolling: "touch",
              maskImage: "linear-gradient(to bottom, transparent 0px, black 20px, black calc(100% - 20px), transparent 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 0px, black 20px, black calc(100% - 20px), transparent 100%)",
            }}
          >
            {/* Classic Resume Template Preview */}
          <div
            className="resume-preview-container"
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #e1e4e8",
              borderRadius: "12px",
              padding: "32px 28px",
              fontFamily: "Roboto",
              fontSize: "14px",
              lineHeight: "1.4",
              marginBottom: "16px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.12)",
              transition: "box-shadow 0.2s ease",
              position: "relative",
              maxWidth: "800px",
              margin: "8px auto 24px auto",
              minHeight: "800px",
            }}
          >
            {/* Header - Personal Info */}
            {data.personalInfo && (
              <>
              {/* <Tooltip
                title={<AIAnalysisTooltip analysisData={data.personalInfo.aiAnalysis} sectionName="personalInfo" />}
                placement={getDynamicPlacement()}
                trigger="hover"
                overlayStyle={getTooltipOverlayStyle()}
                color="white"
                overlayInnerStyle={{ color: '#333' }}
              > */}
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: "20px",
                    borderBottom: "2px solid #333",
                    paddingBottom: "12px",
                    cursor: data.personalInfo.aiAnalysis ? 'pointer' : 'default',
                    position: 'relative',
                  }}
                >
                  {/* Edit Button for Personal Info */}
                  <Button
                    icon={<EditOutlined />}
                    size="small"
                    onClick={onEditPersonalInfo}
                    style={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      fontSize: '14px', 
                      height: '26px', 
                      width: '26px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid #d9d9d9',
                      backgroundColor: '#fff',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      borderRadius: '6px',
                      zIndex: 10
                    }}
                    title="Edit Personal Information"
                  />

                  <Title level={2} style={{ margin: 0, fontSize: "20px", fontWeight: "bold", color: "#333" }}>
                    {data.personalInfo.name || ""}
                    {/* {data.personalInfo.aiAnalysis && (
                      <span style={{
                        marginLeft: '8px',
                        fontSize: '10px',
                        fontWeight: 'normal',
                        backgroundColor: getScoreColor(calculateSectionScore(data.personalInfo.aiAnalysis)),
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '10px'
                      }}>
                        {calculateSectionScore(data.personalInfo.aiAnalysis)}
                      </span>
                    )} */}
                  </Title>
                  <div style={{ marginTop: "8px", fontSize: "12px", color: "#666" }}>
                    {data.personalInfo.email && <span>{data.personalInfo.email}</span>}
                    {data.personalInfo.phone && data.personalInfo.email && <span> • </span>}
                    {data.personalInfo.phone && <span>{data.personalInfo.phone}</span>}
                    {(data.personalInfo.email || data.personalInfo.phone) && data.personalInfo.location && (
                      <span> • </span>
                    )}
                    {data.personalInfo.location && <span>{data.personalInfo.location}</span>}
                  </div>
                  {(data.personalInfo.linkedIn || data.personalInfo.github) && (
                    <div style={{ marginTop: "4px", fontSize: "12px", color: "#666" }}>
                      {data.personalInfo.linkedIn && <span>LinkedIn: {data.personalInfo.linkedIn}</span>}
                      {data.personalInfo.linkedIn && data.personalInfo.github && <span> • </span>}
                      {data.personalInfo.github && <span>GitHub: {data.personalInfo.github}</span>}
                    </div>
                  )}
                </div>
              {/* </Tooltip> */}
              </>
            )}

            {/* Professional Summary */}
            <Tooltip
              title={<AIAnalysisTooltip 
                analysisData={data.summary?.aiAnalysis} 
                sectionName="summary" 
                content={typeof data.summary === 'string' ? data.summary : data.summary?.content}
              />}
              placement={getDynamicPlacement()}
              trigger="hover"
              overlayStyle={getTooltipOverlayStyle()}
              color="white"
              overlayInnerStyle={{ color: '#333' }}
            >
              <div style={{
                marginBottom: "16px",
                cursor: 'default',
                position: 'relative'
              }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: "bold",
                    color: "#333",
                    marginBottom: "8px",
                    borderBottom: "1px solid #ccc",
                    paddingBottom: "3px",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <EditableSectionTitle
                      sectionKey="summary"
                      title={sectionTitles?.summary || 'Professional Summary'}
                      onTitleChange={onSectionTitleChange}
                    />
                    {data.summary?.aiAnalysis && (
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 'normal',
                        backgroundColor: getScoreColor(calculateSectionScore(data.summary.aiAnalysis)),
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '10px'
                      }}>
                        {calculateSectionScore(data.summary.aiAnalysis)}%
                      </span>
                    )}
                    <Button
                      size="small"
                      type="primary"
                      style={{
                        padding: '0 8px',
                        height: '24px',
                        fontSize: '14px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
                        fontWeight: '500',
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveInlineInput('summary');
                      }}
                    >
                      Enhance with AI ✨
                    </Button>
                  </span>

                  {/* Edit button for Summary - aligned with title */}
                  {((typeof data.summary === 'string' && data.summary.trim()) || (typeof data.summary === 'object' && data.summary?.content && data.summary.content.trim())) && (
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={() => onEditSummary(data.summary)}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit Summary"
                    />
                  )}
                </h3>
                {((typeof data.summary === 'string' && data.summary.trim()) || (typeof data.summary === 'object' && data.summary?.content && data.summary.content.trim())) ? (
                  <p style={{ margin: 0, fontSize: "13px", lineHeight: "1.4", textAlign: "justify" }}>
                    {typeof data.summary === 'string' ? data.summary : data.summary?.content || data.summary}
                  </p>
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No professional summary added yet.</Text>
                    <br />
                    <Button 
                      type="dashed" 
                      icon={<PlusOutlined />} 
                      onClick={() => setActiveInlineInput('summary')}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your Professional Summary
                    </Button>
                  </div>
                )}
              </div>
            </Tooltip>

            {/* Inline input for summary section */}
            {activeInlineInput === 'summary' && (
              <div style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #d1ecf1' }}>
                <Input.TextArea
                  placeholder="Enter AI enhancement prompt for the professional summary..."
                  value={inlinePrompt}
                  onChange={(e) => setInlinePrompt(e.target.value)}
                  rows={2}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button 
                    type="primary" 
                    size="small"
                    onClick={(e) => handleInlineEnhance(e, 'summary')}
                    loading={enhancingSection === 'summary'}
                  >
                    Enhance With AI
                  </Button>
                  <Button 
                    size="small"
                    onClick={() => {
                      setActiveInlineInput(null);
                      setInlinePrompt('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Work Experience */}
            <div style={getSectionContainerStyles()}>
              <h3
                style={getSectionHeaderStyles()}
              >
                <EditableSectionTitle
                  sectionKey="experience"
                  title={sectionTitles?.experience || 'Work Experience'}
                  onTitleChange={onSectionTitleChange}
                />
                <Button
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={onAddExperience}
                  style={{
                    fontSize: '14px', 
                    height: '28px', 
                    width: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #d9d9d9',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderRadius: '6px'
                  }}
                  title="Add Experience"
                />
              </h3>
              {resumeData?.parsedData?.experience && resumeData.parsedData.experience.length > 0 ? (
                <DragDropContext onDragEnd={(result) => {
                  if (!result.destination) return;
                  if (result.source.index === result.destination.index) return;
                  const reorderedItems = Array.from(resumeData.parsedData.experience);
                  const [movedItem] = reorderedItems.splice(result.source.index, 1);
                  reorderedItems.splice(result.destination.index, 0, movedItem);
                  onReorderItems("experience", reorderedItems);
                }}>
                  <Droppable droppableId="experience-items">
                    {(droppableProvided, droppableSnapshot) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        style={{
                          background: droppableSnapshot.isDraggingOver ? "#f0f7ff" : "transparent",
                          borderRadius: "4px",
                          transition: "background 0.2s ease",
                        }}
                      >
                        {resumeData.parsedData.experience.map((exp, index) => (
                          <Draggable
                            key={`experience-items-${index}`}
                            draggableId={`experience-items-${index}`}
                            index={index}
                          >
                            {(draggableProvided, draggableSnapshot) => (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  background: draggableSnapshot.isDragging ? "#fff" : "transparent",
                                  boxShadow: draggableSnapshot.isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                                  borderRadius: "4px",
                                }}
                              >
                                <React.Fragment key={`exp-fragment-${index}`}>
                                  <Tooltip
                                    key={index}
                                    title={<AIAnalysisTooltip analysisData={exp.aiAnalysis} sectionName="experience" itemIndex={index} description={exp.description} />}
                                    placement={getDynamicPlacement()}
                                    trigger="hover"
                                    overlayStyle={getTooltipOverlayStyle()}
                                    color="white"
                                    overlayInnerStyle={{ color: '#333' }}
                                  >
                                    <div
                                      style={{
                                        marginBottom: "14px",
                                        cursor: 'default',
                                        position: 'relative'
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "baseline",
                                          marginBottom: "3px",
                                        }}
                                      >
                                        <div style={{
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          alignItems: 'baseline',
                                          gap: '4px',
                                          width: '100%'
                                        }}>
                                          {exp.title && <strong style={{ fontSize: "13px", color: "#333" }}>{exp.title}</strong>}
                                          {exp.aiAnalysis && calculateSectionScore(exp.aiAnalysis) && (
                                            <span style={{
                                              fontSize: '9px',
                                              fontWeight: 'normal',
                                              backgroundColor: getScoreColor(calculateSectionScore(exp.aiAnalysis)),
                                              color: 'white',
                                              padding: '1px 4px',
                                              borderRadius: '8px',
                                              flexShrink: 0
                                            }}>
                                              {calculateSectionScore(exp.aiAnalysis)}%
                                            </span>
                                          )}
                                          <Button
                                            size="small"
                                            type="primary"
                                            style={{
                                              padding: '0 8px',
                                              height: '24px',
                                              fontSize: '14px',
                                              background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '12px',
                                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
                                              fontWeight: '500',
                                              position: 'relative',
                                              overflow: 'hidden',
                                              transition: 'all 0.3s ease',
                                              cursor: 'pointer',
                                              flexShrink: 0
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveInlineInput(`experience-${index}`);
                                            }}
                                          >
                                            Enhance with AI ✨
                                          </Button>
                                        </div>
                                        {(exp.startDate || exp.endDate) && (
                                          <span style={{ fontSize: "12px", color: "#666", fontStyle: "italic", whiteSpace: "nowrap", flexShrink: 0 }}>
                                            {formatDateToMMYYYY(exp.startDate)} - {formatDateToMMYYYY(exp.endDate) || "Present"}
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ fontSize: "12px", color: "#555", marginBottom: "6px" }}>
                                        <em>
                                          {exp.company || ""}
                                          {exp.location && `, ${exp.location}`}
                                        </em>
                                      </div>
                                      {exp.description ? (
                                        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                          <div style={{ flex: 1, marginRight: '8px' }}>
                                            {formatDescriptionBulletsLocal(exp.description)}
                                          </div>
                                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignSelf: 'flex-end' }}>
                                            <DragHandle dragHandleProps={draggableProvided.dragHandleProps} />
                                            <Button
                                              icon={<EditOutlined />}
                                              size="small"
                                              onClick={() => onEditExperience(exp, index)}
                                              style={{
                                                fontSize: '14px',
                                                height: '26px',
                                                width: '26px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '1px solid #d9d9d9',
                                                backgroundColor: '#fff',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                borderRadius: '6px'
                                              }}
                                              title="Edit Experience"
                                            />
                                            <Button
                                              icon={<DeleteOutlined />}
                                              size="small"
                                              danger
                                              onClick={() => onDeleteExperience(index)}
                                              style={{
                                                fontSize: '14px',
                                                height: '26px',
                                                width: '26px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '1px solid #ff7875',
                                                backgroundColor: '#fff',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                borderRadius: '6px'
                                              }}
                                              title="Delete Experience"
                                            />
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                          <DragHandle dragHandleProps={draggableProvided.dragHandleProps} />
                                          <Button
                                            icon={<EditOutlined />}
                                            size="small"
                                            onClick={() => onEditExperience(exp, index)}
                                            style={{
                                              fontSize: '14px',
                                              height: '26px',
                                              width: '26px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              border: '1px solid #d9d9d9',
                                              backgroundColor: '#fff',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                              borderRadius: '6px'
                                            }}
                                            title="Edit Experience"
                                          />
                                          <Button
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            danger
                                            onClick={() => onDeleteExperience(index)}
                                            style={{
                                              fontSize: '14px',
                                              height: '26px',
                                              width: '26px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              border: '1px solid #ff7875',
                                              backgroundColor: '#fff',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                              borderRadius: '6px'
                                            }}
                                            title="Delete Experience"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </Tooltip>

                                  {/* Inline input for this specific experience item */}
                                  {activeInlineInput === `experience-${index}` && (
                                    <div style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #d1ecf1' }}>
                                      <Input.TextArea
                                        placeholder="Enter AI enhancement prompt for this work experience..."
                                        value={inlinePrompt}
                                        onChange={(e) => setInlinePrompt(e.target.value)}
                                        rows={2}
                                        style={{ marginBottom: '8px' }}
                                      />
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <Button
                                          type="primary"
                                          size="small"
                                          onClick={(e) => handleInlineEnhance(e, 'experience', null, index)}
                                          loading={enhancingSection === `experience-${index}`}
                                        >
                                          Enhance With AI
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setActiveInlineInput(null);
                                            setInlinePrompt('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </React.Fragment>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                  <Text>No work experience added yet.</Text>
                  <br />
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />} 
                    onClick={onAddExperience}
                    style={{ marginTop: "8px" }}
                  >
                    Add Your First Experience
                  </Button>
                </div>
              )}
            </div>

            {/* Education */}
            <div style={getSectionContainerStyles()}>
              <h3
                style={getSectionHeaderStyles()}
              >
                <EditableSectionTitle
                  sectionKey="education"
                  title={sectionTitles?.education || 'Education'}
                  onTitleChange={onSectionTitleChange}
                />
                <Button
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={onAddEducation}
                  style={{
                    fontSize: '14px', 
                    height: '28px', 
                    width: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #d9d9d9',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderRadius: '6px'
                  }}
                  title="Add Education"
                />
              </h3>
              {resumeData?.parsedData?.education && resumeData.parsedData.education.length > 0 ? (
                <DragDropContext onDragEnd={(result) => {
                  if (!result.destination) return;
                  if (result.source.index === result.destination.index) return;
                  const reorderedItems = Array.from(resumeData.parsedData.education);
                  const [movedItem] = reorderedItems.splice(result.source.index, 1);
                  reorderedItems.splice(result.destination.index, 0, movedItem);
                  onReorderItems("education", reorderedItems);
                }}>
                  <Droppable droppableId="education-items">
                    {(droppableProvided, droppableSnapshot) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        style={{
                          background: droppableSnapshot.isDraggingOver ? "#f0f7ff" : "transparent",
                          borderRadius: "4px",
                          transition: "background 0.2s ease",
                        }}
                      >
                        {resumeData.parsedData.education.map((edu, index) => (
                          <Draggable
                            key={`education-items-${index}`}
                            draggableId={`education-items-${index}`}
                            index={index}
                          >
                            {(draggableProvided, draggableSnapshot) => (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  background: draggableSnapshot.isDragging ? "#fff" : "transparent",
                                  boxShadow: draggableSnapshot.isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                                  borderRadius: "4px",
                                }}
                              >
                                <div
                                  key={`edu-${index}`}
                                  style={{
                                    marginBottom: "10px",
                                    cursor: 'default',
                                    padding: '0',
                                    borderRadius: '0',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    position: 'relative'
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "baseline",
                                      marginBottom: "3px",
                                    }}
                                  >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                      {edu.degree && <strong style={{ fontSize: "13px", color: "#333" }}>{edu.degree}</strong>}
                                    </div>
                                    {(edu.startDate || edu.endDate || edu.graduationDate) && (
                                      <span style={{ fontSize: "12px", color: "#666", fontStyle: "italic", whiteSpace: "nowrap", flexShrink: 0 }}>
                                        {formatDateToMMYYYY(edu.startDate)}{edu.startDate && (edu.endDate || edu.graduationDate) ? " - " : ""}{formatDateToMMYYYY(edu.endDate || edu.graduationDate)}
                                      </span>
                                    )}
                                  </div>
                                  <div style={{ fontSize: "12px", color: "#555", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                      <em>
                                        {edu.school || ""}
                                        {edu.location && `, ${edu.location}`}
                                      </em>
                                      {edu.gpa && <span style={{ marginLeft: "8px" }}>GPA: {edu.gpa}</span>}
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                      <DragHandle dragHandleProps={draggableProvided.dragHandleProps} />
                                      <Button
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => onEditEducation(edu, index)}
                                        style={{
                                          fontSize: '14px',
                                          height: '26px',
                                          width: '26px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          border: '1px solid #d9d9d9',
                                          backgroundColor: '#fff',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                          borderRadius: '6px'
                                        }}
                                        title="Edit Education"
                                      />
                                      <Button
                                        icon={<DeleteOutlined />}
                                        size="small"
                                        danger
                                        onClick={() => onDeleteEducation(index)}
                                        style={{
                                          fontSize: '14px',
                                          height: '26px',
                                          width: '26px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          border: '1px solid #ff7875',
                                          backgroundColor: '#fff',
                                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                          borderRadius: '6px'
                                        }}
                                        title="Delete Education"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                  <Text>No education added yet.</Text>
                  <br />
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />} 
                    onClick={onAddEducation}
                    style={{ marginTop: "8px" }}
                  >
                    Add Your Education
                  </Button>
                </div>
              )}
            </div>

            {/* Skills */}
            <div style={getSectionContainerStyles()}>
              {((Array.isArray(data.skills) && data.skills.length > 0) || (data.skills?.list && data.skills.list.length > 0) || (data.skills?.categorized)) ? (
                <Tooltip
                  title={<AIAnalysisTooltip analysisData={data.skills?.aiAnalysis} sectionName="skills" />}
                  placement={getDynamicPlacement()}
                  trigger="hover"
                  overlayStyle={getTooltipOverlayStyle()}
                  color="white"
                  overlayInnerStyle={{ color: '#333' }}
                >
                  <div
                    style={{
                      cursor: 'default',
                      position: 'relative'
                    }}
                  >

                    <h3
                      style={{
                        margin: 0,
                        fontSize: "15px",
                        fontWeight: "bold",
                        color: "#333",
                        marginBottom: "8px",
                        borderBottom: "1px solid #ccc",
                        paddingBottom: "3px",
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <EditableSectionTitle
                          sectionKey="skills"
                          title={sectionTitles?.skills || 'Skills'}
                          onTitleChange={onSectionTitleChange}
                        />
                        {data.skills?.aiAnalysis && (
                          <span style={{
                            fontSize: '10px',
                            fontWeight: 'normal',
                            backgroundColor: getScoreColor(calculateSectionScore(data.skills.aiAnalysis)),
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '10px'
                          }}>
                            {calculateSectionScore(data.skills.aiAnalysis)}%
                          </span>
                        )}
                        <Button
                          size="small"
                          type="primary"
                          style={{
                            padding: '0 8px',
                            height: '24px',
                            fontSize: '14px',
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
                            fontWeight: '500',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveInlineInput('skills');
                          }}
                        >
                          Enhance with AI ✨
                        </Button>
                      </span>
                      <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={onEditSkills}
                        style={{
                          fontSize: '14px',
                          height: '28px',
                          width: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #d9d9d9',
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          borderRadius: '6px'
                        }}
                        title="Edit Skills"
                      />
                    </h3>
                    {/* Display categorized skills if available, otherwise display flat list */}
                    {data.skills?.categorized ? (
                      // Categorized display - handle multiple possible category structures
                      <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
                        {/* Handle standard categorization: technical, soft, tools, other */}
                        {data.skills.categorized.technical && data.skills.categorized.technical.length > 0 && (
                          <div style={{ marginBottom: "6px" }}>
                            <strong style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>Technical Skills:</strong>
                            <span style={{ marginLeft: "8px" }}>
                              {data.skills.categorized.technical.join(', ')}
                            </span>
                          </div>
                        )}
                        {data.skills.categorized.soft && data.skills.categorized.soft.length > 0 && (
                          <div style={{ marginBottom: "6px" }}>
                            <strong style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>Soft Skills:</strong>
                            <span style={{ marginLeft: "8px" }}>
                              {data.skills.categorized.soft.join(', ')}
                            </span>
                          </div>
                        )}
                        {data.skills.categorized.tools && data.skills.categorized.tools.length > 0 && (
                          <div style={{ marginBottom: "6px" }}>
                            <strong style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>Tools:</strong>
                            <span style={{ marginLeft: "8px" }}>
                              {data.skills.categorized.tools.join(', ')}
                            </span>
                          </div>
                        )}
                        {data.skills.categorized.other && data.skills.categorized.other.length > 0 && (
                          <div style={{ marginBottom: "6px" }}>
                            <strong style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>Other:</strong>
                            <span style={{ marginLeft: "8px" }}>
                              {data.skills.categorized.other.join(', ')}
                            </span>
                          </div>
                        )}

                        {/* Handle alternative categorization structures that AI might return */}
                        {data.skills.categorized.languages && data.skills.categorized.languages.length > 0 && (
                          <div style={{ marginBottom: "6px" }}>
                            <strong style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>Languages:</strong>
                            <span style={{ marginLeft: "8px" }}>
                              {data.skills.categorized.languages.join(', ')}
                            </span>
                          </div>
                        )}
                        {data.skills.categorized.frameworks && data.skills.categorized.frameworks.length > 0 && (
                          <div style={{ marginBottom: "6px" }}>
                            <strong style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>Frameworks:</strong>
                            <span style={{ marginLeft: "8px" }}>
                              {data.skills.categorized.frameworks.join(', ')}
                            </span>
                          </div>
                        )}
                        {data.skills.categorized.developer_tools && data.skills.categorized.developer_tools.length > 0 && (
                          <div style={{ marginBottom: "6px" }}>
                            <strong style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>Developer Tools:</strong>
                            <span style={{ marginLeft: "8px" }}>
                              {data.skills.categorized.developer_tools.join(', ')}
                            </span>
                          </div>
                        )}
                        {data.skills.categorized.libraries && data.skills.categorized.libraries.length > 0 && (
                          <div style={{ marginBottom: "6px" }}>
                            <strong style={{ color: "#000", fontSize: "12px", fontWeight: "bold" }}>Libraries:</strong>
                            <span style={{ marginLeft: "8px" }}>
                              {data.skills.categorized.libraries.join(', ')}
                            </span>
                          </div>
                        )}

                        {/* Debug: Show all other categories that might exist */}
                        {Object.keys(data.skills.categorized).filter(key =>
                          !['technical', 'soft', 'tools', 'other', 'languages', 'frameworks', 'developer_tools', 'libraries'].includes(key)
                        ).map(key => (
                          data.skills.categorized[key] && data.skills.categorized[key].length > 0 && (
                            <div key={key} style={{ marginBottom: "6px" }}>
                              <strong style={{ color: "#595959", fontSize: "12px" }}>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>
                              <span style={{ marginLeft: "8px" }}>
                                {Array.isArray(data.skills.categorized[key]) ? data.skills.categorized[key].join(', ') : data.skills.categorized[key]}
                              </span>
                            </div>
                          )
                        ))}
                      </div>
                    ) : (
                      // Flat list display (backward compatible)
                      <div
                        style={{
                          fontSize: "13px",
                          lineHeight: "1.1",
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: "2px 8px",
                          columnGap: "8px",
                        }}
                      >
                        {(Array.isArray(data.skills) ? data.skills : data.skills?.list || []).map((skill, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              wordWrap: "break-word",
                              marginBottom: "2px",
                              lineHeight: "1.2",
                            }}
                          >
                            <span style={{ marginRight: "4px", flexShrink: "0" }}>•</span>
                            <span>{skill}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Tooltip>
              ) : (
                <>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "15px",
                      fontWeight: "bold",
                      color: "#333",
                      marginBottom: "8px",
                      borderBottom: "1px solid #ccc",
                      paddingBottom: "3px",
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <EditableSectionTitle
                      sectionKey="skills"
                      title={sectionTitles?.skills || 'Skills'}
                      onTitleChange={onSectionTitleChange}
                    />
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <Button
                        size="small"
                        type="primary"
                        style={{
                          padding: '0 8px',
                          height: '24px',
                          fontSize: '14px',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
                          fontWeight: '500',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer'
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveInlineInput('skills');
                        }}
                      >
                        Enhance with AI ✨
                      </Button>
                      <Button
                        icon={<PlusOutlined />}
                        size="small"
                        onClick={onEditSkills}
                        style={{
                          fontSize: '14px',
                          height: '28px',
                          width: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #d9d9d9',
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          borderRadius: '6px'
                        }}
                        title="Add Skills"
                      />
                    </div>
                  </h3>
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No skills added yet.</Text>
                    <br />
                    <Button 
                      type="dashed" 
                      icon={<PlusOutlined />} 
                      onClick={onEditSkills}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your Skills
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Inline input for skills section */}
            {activeInlineInput === 'skills' && (
              <div style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #d1ecf1' }}>
                <Input.TextArea
                  placeholder="Enter AI enhancement prompt for skills section..."
                  value={inlinePrompt}
                  onChange={(e) => setInlinePrompt(e.target.value)}
                  rows={2}
                  style={{ marginBottom: '8px' }}
                />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Button
                    type="primary"
                    size="small"
                    onClick={(e) => handleInlineEnhance(e, 'skills')}
                    loading={enhancingSection === 'skills'}
                  >
                    Enhance With AI
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setActiveInlineInput(null);
                      setInlinePrompt('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {/* Projects */}
            <div style={getSectionContainerStyles()}>
              <h3
                style={getSectionHeaderStyles()}
              >
                <EditableSectionTitle
                  sectionKey="projects"
                  title={sectionTitles?.projects || 'Projects'}
                  onTitleChange={onSectionTitleChange}
                />
                <Button
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={onAddProject}
                  style={{
                    fontSize: '14px', 
                    height: '28px', 
                    width: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #d9d9d9',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderRadius: '6px'
                  }}
                  title="Add Project"
                />
              </h3>
              {resumeData?.parsedData?.projects && resumeData.parsedData.projects.length > 0 ? (
                <DragDropContext onDragEnd={(result) => {
                  if (!result.destination) return;
                  if (result.source.index === result.destination.index) return;
                  const reorderedItems = Array.from(resumeData.parsedData.projects);
                  const [movedItem] = reorderedItems.splice(result.source.index, 1);
                  reorderedItems.splice(result.destination.index, 0, movedItem);
                  onReorderItems("projects", reorderedItems);
                }}>
                  <Droppable droppableId="projects-items">
                    {(droppableProvided, droppableSnapshot) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        style={{
                          background: droppableSnapshot.isDraggingOver ? "#f0f7ff" : "transparent",
                          borderRadius: "4px",
                          transition: "background 0.2s ease",
                        }}
                      >
                        {resumeData.parsedData.projects.map((project, index) => (
                          <Draggable
                            key={`projects-items-${index}`}
                            draggableId={`projects-items-${index}`}
                            index={index}
                          >
                            {(draggableProvided, draggableSnapshot) => (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  background: draggableSnapshot.isDragging ? "#fff" : "transparent",
                                  boxShadow: draggableSnapshot.isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                                  borderRadius: "4px",
                                }}
                              >
                                <React.Fragment key={`project-fragment-${index}`}>
                                  <Tooltip
                                    title={<AIAnalysisTooltip analysisData={project.aiAnalysis} sectionName="projects" itemIndex={index} description={project.description} />}
                                    placement={getDynamicPlacement()}
                                    trigger="hover"
                                    overlayStyle={getTooltipOverlayStyle()}
                                    color="white"
                                    overlayInnerStyle={{ color: '#333' }}
                                  >
                                    <div
                                      style={{
                                        marginBottom: "12px",
                                        cursor: 'default',
                                        position: 'relative'
                                      }}
                                    >
                                      <div style={{ display: "flex", alignItems: "baseline", marginBottom: "3px" }}>
                                        <div style={{
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          alignItems: 'baseline',
                                          gap: '4px',
                                          width: '100%'
                                        }}>
                                          {(project.name || project.title) && (
                                            <strong style={{ fontSize: "13px", color: "#333" }}>
                                              {project.name || project.title}
                                            </strong>
                                          )}
                                          {project.link && project.link !== "null" && project.link !== null && project.link.trim() !== "" && (
                                            <a
                                              href={project.link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                color: "#333",
                                                flexShrink: 0
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <LinkOutlined style={{ fontSize: "12px" }} />
                                            </a>
                                          )}
                                          {project.aiAnalysis && calculateSectionScore(project.aiAnalysis) && (
                                            <span style={{
                                              fontSize: '9px',
                                              fontWeight: 'normal',
                                              backgroundColor: getScoreColor(calculateSectionScore(project.aiAnalysis)),
                                              color: 'white',
                                              padding: '1px 4px',
                                              borderRadius: '8px',
                                              flexShrink: 0
                                            }}>
                                              {calculateSectionScore(project.aiAnalysis)}%
                                            </span>
                                          )}
                                          <Button
                                            size="small"
                                            type="primary"
                                            style={{
                                              padding: '0 8px',
                                              height: '24px',
                                              fontSize: '14px',
                                              background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '12px',
                                              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
                                              fontWeight: '500',
                                              position: 'relative',
                                              overflow: 'hidden',
                                              transition: 'all 0.3s ease',
                                              cursor: 'pointer',
                                              flexShrink: 0
                                            }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveInlineInput(`projects-${index}`);
                                            }}
                                          >
                                            Enhance with AI ✨
                                          </Button>
                                        </div>
                                      </div>
                                      {project.technologies && project.technologies.length > 0 && (
                                        <div style={{ fontSize: "12px", color: "#666", fontStyle: "italic", marginTop: "8px" }}>
                                          Technologies: {project.technologies.join(", ")}
                                        </div>
                                      )}
                                      {project.description ? (
                                        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                                          <div style={{ flex: 1, marginRight: '8px' }}>
                                            {formatDescriptionBulletsLocal(project.description)}
                                          </div>
                                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0, alignSelf: 'flex-end' }}>
                                            <DragHandle dragHandleProps={draggableProvided.dragHandleProps} />
                                            <Button
                                              icon={<EditOutlined />}
                                              size="small"
                                              onClick={() => onEditProject(project, index)}
                                              style={{
                                                fontSize: '14px',
                                                height: '26px',
                                                width: '26px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '1px solid #d9d9d9',
                                                backgroundColor: '#fff',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                borderRadius: '6px'
                                              }}
                                              title="Edit Project"
                                            />
                                            <Button
                                              icon={<DeleteOutlined />}
                                              size="small"
                                              danger
                                              onClick={() => onDeleteProject(index)}
                                              style={{
                                                fontSize: '14px',
                                                height: '26px',
                                                width: '26px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '1px solid #ff7875',
                                                backgroundColor: '#fff',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                borderRadius: '6px'
                                              }}
                                              title="Delete Project"
                                            />
                                          </div>
                                        </div>
                                      ) : (
                                        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                          <DragHandle dragHandleProps={draggableProvided.dragHandleProps} />
                                          <Button
                                            icon={<EditOutlined />}
                                            size="small"
                                            onClick={() => onEditProject(project, index)}
                                            style={{
                                              fontSize: '14px',
                                              height: '26px',
                                              width: '26px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              border: '1px solid #d9d9d9',
                                              backgroundColor: '#fff',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                              borderRadius: '6px'
                                            }}
                                            title="Edit Project"
                                          />
                                          <Button
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            danger
                                            onClick={() => onDeleteProject(index)}
                                            style={{
                                              fontSize: '14px',
                                              height: '26px',
                                              width: '26px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              border: '1px solid #ff7875',
                                              backgroundColor: '#fff',
                                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                              borderRadius: '6px'
                                            }}
                                            title="Delete Project"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </Tooltip>
                                  {activeInlineInput === `projects-${index}` && (
                                    <div style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #d1ecf1' }}>
                                      <Input.TextArea
                                        placeholder="Enter AI enhancement prompt for this project..."
                                        value={inlinePrompt}
                                        onChange={(e) => setInlinePrompt(e.target.value)}
                                        rows={2}
                                        style={{ marginBottom: '8px' }}
                                      />
                                      <div style={{ display: 'flex', gap: '8px' }}>
                                        <Button
                                          type="primary"
                                          size="small"
                                          onClick={(e) => handleInlineEnhance(e, 'projects', null, index)}
                                          loading={enhancingSection === `projects-${index}`}
                                        >
                                          Enhance With AI
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={() => {
                                            setActiveInlineInput(null);
                                            setInlinePrompt('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </React.Fragment>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                  <Text>No projects added yet.</Text>
                  <br />
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />} 
                    onClick={onAddProject}
                    style={{ marginTop: "8px" }}
                  >
                    Add Your First Project
                  </Button>
                </div>
              )}
            </div>


            {/* Certifications */}
            <div style={getSectionContainerStyles()}>
              <h3
                style={getSectionHeaderStyles()}
              >
                <EditableSectionTitle
                  sectionKey="certifications"
                  title={sectionTitles?.certifications || 'Certifications'}
                  onTitleChange={onSectionTitleChange}
                />
                <Button
                  icon={<PlusOutlined />}
                  size="small"
                  onClick={onAddCertification}
                  style={{
                    fontSize: '14px', 
                    height: '28px', 
                    width: '28px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #d9d9d9',
                    backgroundColor: '#fff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderRadius: '6px'
                  }}
                  title="Add Certification"
                />
              </h3>
              {resumeData?.parsedData?.certifications && resumeData.parsedData.certifications.length > 0 ? (
                <DragDropContext onDragEnd={(result) => {
                  if (!result.destination) return;
                  if (result.source.index === result.destination.index) return;
                  const reorderedItems = Array.from(resumeData.parsedData.certifications);
                  const [movedItem] = reorderedItems.splice(result.source.index, 1);
                  reorderedItems.splice(result.destination.index, 0, movedItem);
                  onReorderItems("certifications", reorderedItems);
                }}>
                  <Droppable droppableId="certifications-items">
                    {(droppableProvided, droppableSnapshot) => (
                      <div
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        style={{
                          background: droppableSnapshot.isDraggingOver ? "#f0f7ff" : "transparent",
                          borderRadius: "4px",
                          transition: "background 0.2s ease",
                        }}
                      >
                        {resumeData.parsedData.certifications.map((cert, index) => (
                          <Draggable
                            key={`certifications-items-${index}`}
                            draggableId={`certifications-items-${index}`}
                            index={index}
                          >
                            {(draggableProvided, draggableSnapshot) => (
                              <div
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                style={{
                                  ...draggableProvided.draggableProps.style,
                                  background: draggableSnapshot.isDragging ? "#fff" : "transparent",
                                  boxShadow: draggableSnapshot.isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : "none",
                                  borderRadius: "4px",
                                }}
                              >
                                <div
                                  key={`cert-${index}`}
                                  style={{
                                    marginBottom: "8px",
                                    marginTop: index === 0 ? "4px" : "0",
                                    cursor: 'default',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                >
                                  {/* Left side: Certification content */}
                                  <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                    <span style={{ marginRight: '8px', color: '#333', fontSize: '12px' }}>•</span>
                                    <span style={{ fontSize: "12px", color: "#333" }}>
                                      {cert.name}
                                      {cert.issuer && <span style={{ color: '#555' }}> - {cert.issuer}</span>}
                                      {cert.date && <span style={{ color: '#666' }}> ({formatDateToMMYYYY(cert.date)})</span>}
                                    </span>
                                  </div>
                                  {/* Right side: DragHandle/Edit/Delete buttons */}
                                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '12px' }}>
                                    <DragHandle dragHandleProps={draggableProvided.dragHandleProps} />
                                    <Button
                                      icon={<EditOutlined />}
                                      size="small"
                                      onClick={() => onEditCertification(cert, index)}
                                      style={{
                                        fontSize: '14px',
                                        height: '26px',
                                        width: '26px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #d9d9d9',
                                        backgroundColor: '#fff',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        borderRadius: '6px'
                                      }}
                                      title="Edit Certification"
                                    />
                                    <Button
                                      icon={<DeleteOutlined />}
                                      size="small"
                                      danger
                                      onClick={() => onDeleteCertification(index)}
                                      style={{
                                        fontSize: '14px',
                                        height: '26px',
                                        width: '26px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px solid #ff7875',
                                        backgroundColor: '#fff',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        borderRadius: '6px'
                                      }}
                                      title="Delete Certification"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : (
                <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                  <Text>No certifications added yet.</Text>
                  <br />
                  <Button 
                    type="dashed" 
                    icon={<PlusOutlined />} 
                    onClick={onAddCertification}
                    style={{ marginTop: "8px" }}
                  >
                    Add Your First Certification
                  </Button>
                </div>
              )}
            </div>

            {/* Achievements - Only show if added via Add Section */}
            {visibleSections.includes('achievements') && (
              <div style={getSectionContainerStyles()}>
                <h3
                  style={getSectionHeaderStyles()}
                >
                  <EditableSectionTitle
                    sectionKey="achievements"
                    title={sectionTitles?.achievements || 'Achievements'}
                    onTitleChange={onSectionTitleChange}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={onEditAchievements}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit Achievements"
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDeleteSection('achievements')}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ff7875',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Delete Achievements Section"
                    />
                  </div>
                </h3>
                {data.achievements && data.achievements.length > 0 ? (
                  data.achievements.map((achievement, index) => (
                    <div key={index}
                      style={{
                        marginBottom: "8px",
                        cursor: 'default',
                        padding: '0',
                        borderRadius: '0',
                        border: 'none',
                        backgroundColor: 'transparent'
                      }}
                    >
                      {achievement.description && (
                        <div style={{ marginTop: '8px' }}>
                          {formatAchievementBulletsLocal(achievement.description)}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No achievements added yet.</Text>
                    <br />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={onEditAchievements}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your First Achievement
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Languages - Only show if added via Add Section */}
            {visibleSections.includes('languages') && (
              <div style={getSectionContainerStyles()}>
                <h3
                  style={getSectionHeaderStyles()}
                >
                  <EditableSectionTitle
                    sectionKey="languages"
                    title={sectionTitles?.languages || 'Languages'}
                    onTitleChange={onSectionTitleChange}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={onEditLanguages}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit Languages"
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDeleteSection('languages')}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ff7875',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Delete Languages Section"
                    />
                  </div>
                </h3>
                {data.languages && data.languages.length > 0 ? (
                  data.languages.map((language, index) => (
                    <div key={index}
                      style={{
                        marginBottom: "8px",
                        cursor: 'default',
                        padding: '0',
                        borderRadius: '0',
                        border: 'none',
                        backgroundColor: 'transparent'
                      }}
                    >
                      <Text strong>{language.name}</Text>
                      {language.proficiency && (
                        <Text type="secondary" style={{ marginLeft: '8px' }}>
                          - {language.proficiency}
                        </Text>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No languages added yet.</Text>
                    <br />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={onEditLanguages}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your First Language
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Awards - Only show if added via Add Section */}
            {visibleSections.includes('awards') && (
              <div style={getSectionContainerStyles()}>
                <h3
                  style={getSectionHeaderStyles()}
                >
                  <EditableSectionTitle
                    sectionKey="awards"
                    title={sectionTitles?.awards || 'Awards'}
                    onTitleChange={onSectionTitleChange}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={onEditAwards}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit Awards"
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDeleteSection('awards')}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ff7875',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Delete Awards Section"
                    />
                  </div>
                </h3>
                {data.awards && data.awards.length > 0 ? (
                  data.awards.map((award, index) => (
                    <div key={index}
                      style={{
                        marginBottom: "12px",
                        cursor: 'default',
                        padding: '0',
                        borderRadius: '0',
                        border: 'none',
                        backgroundColor: 'transparent'
                      }}
                    >
                      {/* Row 1: Title + Date on right */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                        <Text strong style={{ fontSize: '13px', color: '#333' }}>{award.title}</Text>
                        {award.date && (
                          <Text type="secondary" style={{ fontSize: '12px', flexShrink: 0, marginLeft: '8px' }}>{award.date}</Text>
                        )}
                      </div>
                      {/* Row 2: Issuer */}
                      {award.issuer && (
                        <div style={{ marginBottom: '2px' }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>{award.issuer}</Text>
                        </div>
                      )}
                      {/* Row 3: Description */}
                      {award.description && (
                        <div style={{ marginTop: '4px' }}>
                          <Text style={{ fontSize: '12px', color: '#555', lineHeight: '1.5' }}>{award.description}</Text>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No awards added yet.</Text>
                    <br />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={onEditAwards}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your First Award
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Volunteering - Only show if added via Add Section */}
            {visibleSections.includes('volunteering') && (
              <div style={getSectionContainerStyles()}>
                <h3
                  style={getSectionHeaderStyles()}
                >
                  <EditableSectionTitle
                    sectionKey="volunteering"
                    title={sectionTitles?.volunteering || 'Volunteering'}
                    onTitleChange={onSectionTitleChange}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={onEditVolunteering}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit Volunteering"
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDeleteSection('volunteering')}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ff7875',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Delete Volunteering Section"
                    />
                  </div>
                </h3>
                {data.volunteering && data.volunteering.length > 0 ? (
                  data.volunteering.map((vol, index) => (
                    <div key={index}
                      style={{
                        marginBottom: "14px",
                        cursor: 'default',
                        position: 'relative'
                      }}
                    >
                      {/* Row 1: Organization (bold) + Date on right */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                        <Text strong style={{ fontSize: '13px', color: '#333' }}>{vol.organization}</Text>
                        {(vol.startDate || vol.endDate) && (
                          <Text type="secondary" style={{ fontSize: '12px', flexShrink: 0, marginLeft: '8px' }}>
                            {vol.startDate} - {vol.isCurrentRole ? 'Present' : vol.endDate}
                          </Text>
                        )}
                      </div>
                      {/* Row 2: Role (light tone) + Location */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '4px' }}>
                        {vol.role && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>{vol.role}</Text>
                        )}
                        {vol.location && (
                          <Text type="secondary" style={{ fontSize: '12px', flexShrink: 0, marginLeft: '8px' }}>{vol.location}</Text>
                        )}
                      </div>
                      {/* Row 3: Description */}
                      {vol.description && (
                        <div style={{ marginTop: '4px' }}>
                          <Text style={{ fontSize: '12px', color: '#555', lineHeight: '1.5' }}>{vol.description}</Text>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No volunteering experiences added yet.</Text>
                    <br />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={onEditVolunteering}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your First Volunteering Experience
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Interests - Only show if added via Add Section */}
            {visibleSections.includes('interests') && (
              <div style={getSectionContainerStyles()}>
                <h3
                  style={getSectionHeaderStyles()}
                >
                  <EditableSectionTitle
                    sectionKey="interests"
                    title={sectionTitles?.interests || 'Interests'}
                    onTitleChange={onSectionTitleChange}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={onEditInterests}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit Interests"
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDeleteSection('interests')}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ff7875',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Delete Interests Section"
                    />
                  </div>
                </h3>
                {data.interests && data.interests.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {data.interests.map((interest, index) => (
                      <div key={index}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '4px 12px',
                          backgroundColor: '#f5f5f5',
                          borderRadius: '16px',
                          fontSize: '12px'
                        }}
                      >
                        <span style={{ marginRight: '4px', color: '#333' }}>•</span>
                        <Text style={{ fontSize: '12px' }}>{interest.name}</Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No interests added yet.</Text>
                    <br />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={onEditInterests}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your First Interest
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Industry Expertise - Only show if added via Add Section */}
            {visibleSections.includes('industryExpertise') && (
              <div style={getSectionContainerStyles()}>
                <h3
                  style={getSectionHeaderStyles()}
                >
                  <EditableSectionTitle
                    sectionKey="industryExpertise"
                    title={sectionTitles?.industryExpertise || 'Industry Expertise'}
                    onTitleChange={onSectionTitleChange}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={onEditIndustryExpertise}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit Industry Expertise"
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDeleteSection('industryExpertise')}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ff7875',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Delete Industry Expertise Section"
                    />
                  </div>
                </h3>
                {data.industryExpertise && data.industryExpertise.length > 0 ? (
                  <div>
                    {data.industryExpertise.map((exp, index) => (
                      <div key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '6px',
                          padding: '6px 0',
                          borderBottom: index < data.industryExpertise.length - 1 ? '1px solid #f0f0f0' : 'none'
                        }}
                      >
                        {/* Left side: Name */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px', color: '#333', fontSize: '12px' }}>•</span>
                          <Text strong style={{ fontSize: '13px', color: '#333' }}>{exp.name}</Text>
                        </div>
                        {/* Right side: Proficiency + Years */}
                        {(exp.proficiency || exp.yearsOfExperience) && (
                          <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: '8px' }}>
                            {exp.proficiency && (
                              <Text type="secondary" style={{ fontSize: '12px', color: '#666' }}>
                                {exp.proficiency}
                              </Text>
                            )}
                            {exp.proficiency && exp.yearsOfExperience && (
                              <Text type="secondary" style={{ fontSize: '12px', color: '#999', margin: '0 6px' }}>•</Text>
                            )}
                            {exp.yearsOfExperience && (
                              <Text type="secondary" style={{ fontSize: '12px', color: '#888' }}>
                                {exp.yearsOfExperience} years
                              </Text>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No industry expertise added yet.</Text>
                    <br />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={onEditIndustryExpertise}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your First Expertise
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Publications - Only show if added via Add Section */}
            {visibleSections.includes('publications') && (
              <div style={getSectionContainerStyles()}>
                <h3
                  style={getSectionHeaderStyles()}
                >
                  <EditableSectionTitle
                    sectionKey="publications"
                    title={sectionTitles?.publications || 'Publications'}
                    onTitleChange={onSectionTitleChange}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={onEditPublications}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit Publications"
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDeleteSection('publications')}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ff7875',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Delete Publications Section"
                    />
                  </div>
                </h3>
                {data.publications && data.publications.length > 0 ? (
                  data.publications.map((pub, index) => (
                    <div key={index}
                      style={{
                        marginBottom: "14px",
                        cursor: 'default',
                        position: 'relative'
                      }}
                    >
                      {/* Row 1: Title + Date */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                        <Text strong style={{ fontSize: '13px', color: '#333' }}>{pub.title}</Text>
                        {pub.date && (
                          <Text type="secondary" style={{ fontSize: '12px', flexShrink: 0, marginLeft: '8px' }}>
                            {pub.date}
                          </Text>
                        )}
                      </div>
                      {/* Row 2: Journal/Publication */}
                      {pub.journal && (
                        <div style={{ marginBottom: '2px' }}>
                          <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>{pub.journal}</Text>
                        </div>
                      )}
                      {/* Row 3: Description */}
                      {pub.description && (
                        <div style={{ marginTop: '4px' }}>
                          <Text style={{ fontSize: '12px', color: '#555', lineHeight: '1.5' }}>{pub.description}</Text>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No publications added yet.</Text>
                    <br />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={onEditPublications}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your First Publication
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* References - Only show if added via Add Section */}
            {visibleSections.includes('references') && (
              <div style={getSectionContainerStyles()}>
                <h3
                  style={getSectionHeaderStyles()}
                >
                  <EditableSectionTitle
                    sectionKey="references"
                    title={sectionTitles?.references || 'References'}
                    onTitleChange={onSectionTitleChange}
                  />
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <Button
                      icon={<EditOutlined />}
                      size="small"
                      onClick={onEditReferences}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #d9d9d9',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Edit References"
                    />
                    <Button
                      icon={<DeleteOutlined />}
                      size="small"
                      danger
                      onClick={() => onDeleteSection('references')}
                      style={{
                        fontSize: '14px',
                        height: '26px',
                        width: '26px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ff7875',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        borderRadius: '6px'
                      }}
                      title="Delete References Section"
                    />
                  </div>
                </h3>
                {data.references && data.references.length > 0 ? (
                  data.references.map((ref, index) => (
                    <div key={index}
                      style={{
                        marginBottom: "12px",
                        cursor: 'default',
                        padding: '0',
                        borderRadius: '0',
                        border: 'none',
                        backgroundColor: 'transparent'
                      }}
                    >
                      {/* Row 1: Name + Email on right */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                        <Text strong style={{ fontSize: '13px', color: '#333' }}>{ref.name}</Text>
                        {ref.email && (
                          <Text type="secondary" style={{ fontSize: '12px', flexShrink: 0, marginLeft: '8px' }}>
                            {ref.email}
                          </Text>
                        )}
                      </div>
                      {/* Row 2: Title - Company + Phone on right */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                        {(ref.title || ref.company) && (
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {ref.title}{ref.title && ref.company ? ' - ' : ''}{ref.company}
                          </Text>
                        )}
                        {ref.phone && (
                          <Text type="secondary" style={{ fontSize: '12px', flexShrink: 0, marginLeft: '8px' }}>
                            {ref.phone}
                          </Text>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                    <Text>No references added yet.</Text>
                    <br />
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onClick={onEditReferences}
                      style={{ marginTop: "8px" }}
                    >
                      Add Your First Reference
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Custom Sections - Dynamically rendered based on customSections array */}
            {data.customSections && data.customSections.length > 0 && data.customSections.map((customSection) => (
              visibleSections.includes(customSection.id) && (
                <div key={customSection.id} style={getSectionContainerStyles()}>
                  <h3
                    style={getSectionHeaderStyles()}
                  >
                    <span>{customSection.title?.toUpperCase()}</span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <Button
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => onEditCustomSection(customSection.id)}
                        style={{
                          fontSize: '14px',
                          height: '26px',
                          width: '26px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #d9d9d9',
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          borderRadius: '6px'
                        }}
                        title={`Edit ${customSection.title}`}
                      />
                      <Button
                        icon={<DeleteOutlined />}
                        size="small"
                        danger
                        onClick={() => onDeleteCustomSection(customSection.id)}
                        style={{
                          fontSize: '14px',
                          height: '26px',
                          width: '26px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid #ff4d4f',
                          backgroundColor: '#fff',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                          borderRadius: '6px'
                        }}
                        title={`Delete ${customSection.title}`}
                      />
                    </div>
                  </h3>
                  {customSection.items && customSection.items.length > 0 ? (
                    customSection.items.map((item, itemIndex) => (
                      <div key={itemIndex}
                        style={{
                          marginBottom: "8px",
                          cursor: 'default',
                          padding: '0',
                          borderRadius: '0',
                          border: 'none',
                          backgroundColor: 'transparent'
                        }}
                      >
                        {item.content && (
                          <div style={{ marginTop: '4px' }}>
                            {formatAchievementBulletsLocal(item.content)}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
                      <Text>No items in this section yet.</Text>
                    </div>
                  )}
                </div>
              )
            ))}

            {/* Add New Section Button */}
            <div
              style={{
                marginTop: '24px',
                marginBottom: '24px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                type="dashed"
                icon={<AppstoreAddOutlined />}
                onClick={onAddSection}
                size="large"
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: '56px',
                  fontSize: '15px',
                  borderRadius: '12px',
                  border: '2px dashed #d9d9d9',
                  color: '#595959',
                  backgroundColor: '#fafafa',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#1890ff';
                  e.currentTarget.style.color = '#1890ff';
                  e.currentTarget.style.backgroundColor = '#e6f7ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d9d9d9';
                  e.currentTarget.style.color = '#595959';
                  e.currentTarget.style.backgroundColor = '#fafafa';
                }}
              >
                Add New Section
              </Button>
            </div>

            {/* Inline input for experience sections */}
            {data.experience && data.experience.map((exp, index) => {
              const shouldShow = activeInlineInput === `experience-${index}`;
              return shouldShow && (
                <div key={`inline-experience-${index}`} style={{ margin: '8px 0', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px', border: '1px solid #d1ecf1' }}>
                  <Input.TextArea
                    placeholder="Enter AI enhancement prompt for this work experience..."
                    value={inlinePrompt}
                    onChange={(e) => setInlinePrompt(e.target.value)}
                    rows={2}
                    style={{ marginBottom: '8px' }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      type="primary"
                      size="small"
                      onClick={(e) => handleInlineEnhance(e, 'experience', null, index)}
                      loading={enhancingSection === `experience-${index}`}
                    >
                      Enhance With AI
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setActiveInlineInput(null);
                        setInlinePrompt('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              );
            })}


            {/* Spacing to ensure scrolling */}
            <div style={{ height: "200px" }}></div>
          </div>
        </div>
        ) : (
          /* Cover Letter Content */
          <div
            style={{
              flex: 1,
              overflowY: "scroll",
              overflowX: "hidden",
              padding: "16px",
              height: "auto",
            }}
          >
            <InlineCoverLetterForm
              resumeData={resumeData}
              onSuccess={(updatedResume) => {
                // Update resume data with the new cover letter
                // Merge with existing data to preserve parsedData
                if (updatedResume) {
                  setResumeData(prev => ({
                    ...prev,
                    ...updatedResume,
                    parsedData: updatedResume.parsedData || prev?.parsedData
                  }));
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const ModalBasedResume = () => {
  const { resumeId } = useParams();
  const navigate = useNavigate();
  const fetch = useAuthenticatedFetch();
  const { isAuthenticated } = useAuth();

  // Credit system integration (only for display purposes - no checks for manual edits)
  const {
    balance: creditBalance,
  } = useCredits();

  // Manual edits and adding sections are now FREE - no credit checks needed
  const canManualEdit = true;
  const canAddSection = true;
  const manualEditCost = 0;

  const addSectionCost = 0;

  const [loading, setLoading] = useState(true);
  const [resumeData, setResumeData] = useState(null);
  const [error, setError] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Dynamic tooltip positioning hook
  const { getDynamicPlacement, getTooltipOverlayStyle } = useTooltipPlacement();

  // Delete confirmation hook
  const { showDeleteConfirmation, DeleteModal } = useDeleteConfirmation();

  // Modal states
  const [personalInfoModal, setPersonalInfoModal] = useState(false);
  const [summaryModal, setSummaryModal] = useState(false);
  const [experienceModal, setExperienceModal] = useState(false);
  const [educationModal, setEducationModal] = useState(false);
  const [skillsModal, setSkillsModal] = useState(false);
  const [projectModal, setProjectModal] = useState(false);
  const [certificationModal, setCertificationModal] = useState(false);
  const [achievementsModal, setAchievementsModal] = useState(false);
  const [languagesModal, setLanguagesModal] = useState(false);
  const [awardsModal, setAwardsModal] = useState(false);
  const [addSectionModal, setAddSectionModal] = useState(false);
  const [customSectionModal, setCustomSectionModal] = useState(false);

  // New section modals
  const [volunteeringModal, setVolunteeringModal] = useState(false);
  const [interestsModal, setInterestsModal] = useState(false);
  const [industryExpertiseModal, setIndustryExpertiseModal] = useState(false);
  const [publicationsModal, setPublicationsModal] = useState(false);
  const [referencesModal, setReferencesModal] = useState(false);
  const [editingCustomSectionId, setEditingCustomSectionId] = useState(null); // Track which custom section is being edited

  // Track which sections are visible/added to the resume
  const [visibleSections, setVisibleSections] = useState([
    'summary', 'experience', 'education', 'skills', 'projects', 'certifications'
  ]);

  // Custom section titles - allows users to rename default section headers
  const [sectionTitles, setSectionTitles] = useState({
    summary: 'Professional Summary',
    experience: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certifications',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards',
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry Expertise',
    publications: 'Publications',
    references: 'References',
  });

  // Public Resume states
  const [publicResumeModal, setPublicResumeModal] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [publicUrl, setPublicUrl] = useState('');

  // Edit Mode state (Resume/Cover Letter)
  const [editMode, setEditMode] = useState('resume'); // 'resume' or 'coverLetter'

  // AI Enhancement states
  const [aiEnhanceModal, setAiEnhanceModal] = useState(false);
  const [aiEnhanceLoading, setAiEnhanceLoading] = useState(false);
  
  // Inline AI Enhancement states
  const [activeInlineInput, setActiveInlineInput] = useState(null);
  const [inlinePrompt, setInlinePrompt] = useState("");
  const [enhancingSection, setEnhancingSection] = useState(null);
  
  // Form-specific AI Enhancement prompts
  const [summaryEnhancePrompt, setSummaryEnhancePrompt] = useState("");
  const [experienceEnhancePrompt, setExperienceEnhancePrompt] = useState("");
  const [projectEnhancePrompt, setProjectEnhancePrompt] = useState("");
  
  // Form-specific AI Enhancement visibility states
  const [showSummaryEnhance, setShowSummaryEnhance] = useState(false);
  const [showExperienceEnhance, setShowExperienceEnhance] = useState(false);
  const [showProjectEnhance, setShowProjectEnhance] = useState(false);
  const [aiEnhanceData, setAiEnhanceData] = useState(null);
  const [aiEnhanceType, setAiEnhanceType] = useState(''); // 'summary', 'experience', 'education', 'projects'
  const [aiEnhanceIndex, setAiEnhanceIndex] = useState(null);

  // Temporary AI analysis storage for new entries
  const [tempExperienceAIAnalysis, setTempExperienceAIAnalysis] = useState(null);
  const [tempProjectAIAnalysis, setTempProjectAIAnalysis] = useState(null);
  const [aiEnhanceForm] = Form.useForm();

  // Form states
  const [personalInfoForm] = Form.useForm();
  const [summaryForm] = Form.useForm();
  const [experienceForm] = Form.useForm();
  const [educationForm] = Form.useForm();
  const [projectForm] = Form.useForm();
  const [certificationForm] = Form.useForm();
  const [achievementsForm] = Form.useForm();

  // Edit states
  const [editingExperience, setEditingExperience] = useState(null);
  const [editingEducation, setEditingEducation] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [editingCertification, setEditingCertification] = useState(null);
  const [editingAchievement, setEditingAchievement] = useState(null);
  const [tempAchievements, setTempAchievements] = useState([]);
  const [tempLanguages, setTempLanguages] = useState([]);
  const [tempAwards, setTempAwards] = useState([]);
  const [tempCustomSection, setTempCustomSection] = useState({ title: '', items: [] });

  // New section temp states
  const [tempVolunteering, setTempVolunteering] = useState([]);
  const [tempInterests, setTempInterests] = useState([]);
  const [tempIndustryExpertise, setTempIndustryExpertise] = useState([]);
  const [tempPublications, setTempPublications] = useState([]);
  const [tempReferences, setTempReferences] = useState([]);
  const [skillInput, setSkillInput] = useState('');
  const [tempSkills, setTempSkills] = useState([]);

  // Categorized skills state
  const [tempCategorizedSkills, setTempCategorizedSkills] = useState({
    technical: [],
    soft: [],
    tools: [],
    other: []
  });
  const [categoryInput, setCategoryInput] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('technical');
  
  // Track "currently work here" checkbox state to disable end date
  const [isCurrentJob, setIsCurrentJob] = useState(false);

  useEffect(() => {
    if (resumeId) {
      fetchResumeData();
    }
  }, [resumeId]);

  const fetchResumeData = async () => {
    try {
      const response = await fetch.get(`/resume/${resumeId}`);
      if (!response.success) {
        throw new Error("Failed to fetch resume data");
      }
      setResumeData(response.data);

      // Update visibleSections based on existing data
      // Sections that already have data should be shown
      const parsedData = response.data?.parsedData || {};
      const sectionsWithData = ['summary', 'experience', 'education', 'skills', 'projects', 'certifications'];

      // Add optional sections only if they have data
      if (parsedData.achievements && parsedData.achievements.length > 0) {
        sectionsWithData.push('achievements');
      }
      if (parsedData.languages && parsedData.languages.length > 0) {
        sectionsWithData.push('languages');
      }
      if (parsedData.awards && parsedData.awards.length > 0) {
        sectionsWithData.push('awards');
      }
      if (parsedData.publications && parsedData.publications.length > 0) {
        sectionsWithData.push('publications');
      }
      if (parsedData.references && parsedData.references.length > 0) {
        sectionsWithData.push('references');
      }
      if (parsedData.volunteering && parsedData.volunteering.length > 0) {
        sectionsWithData.push('volunteering');
      }
      if (parsedData.interests && parsedData.interests.length > 0) {
        sectionsWithData.push('interests');
      }

      // Add custom sections - include their IDs if they have data
      if (parsedData.customSections && parsedData.customSections.length > 0) {
        parsedData.customSections.forEach(customSection => {
          if (customSection.id && customSection.items?.length > 0) {
            sectionsWithData.push(customSection.id);
          }
        });
      }

      setVisibleSections(sectionsWithData);

      // Load custom section titles if they exist
      if (parsedData.sectionTitles) {
        setSectionTitles(prev => ({
          ...prev,
          ...parsedData.sectionTitles
        }));
      }

      // Load public status
      setIsPublic(response.data?.isPublic || false);
      if (response.data?.publicSlug) {
        setPublicUrl(`${window.location.origin}/r/${response.data.publicSlug}`);
      }
    } catch (err) {
      // Auth errors (401/403) are handled by Axios interceptor with redirects
      // Only set error for other types of errors
      const errorCode = err?.response?.data?.code || err?.response?.status;
      if (errorCode !== 401 && errorCode !== 403) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle public status change
  const handlePublicStatusChange = (newStatus, data) => {
    setIsPublic(newStatus);
    if (data?.publicUrl) {
      setPublicUrl(data.publicUrl);
    }

    // Refresh resume data to get updated status
    fetchResumeData();
  };

  const handlePreviewResume = async () => {
    // Check if user is authenticated - if not, redirect to login
    const authToken = localStorage.getItem("authorization");
    const adminToken = sessionStorage.getItem("adminResumeAccessToken");
    if (!authToken && !adminToken && !isAuthenticated) {
      // Redirect to login, after which user will go to resume preview page
      const returnUrl = encodeURIComponent(`/app/resume/${resumeId}/preview`);
      navigate(`/app/login?returnUrl=${returnUrl}`);
      return;
    }

    // Track preview action (first time only)
    try {
      await fetch.post(`/resume/${resumeId}/track-preview`);
    } catch (error) {
      // Don't block navigation if tracking fails
    }

    navigate(`/app/resume/${resumeId}/preview`);
  };



  const saveChanges = async () => {
    if (!hasChanges) return;

    try {
      const response = await fetch.put(`/resume/${resumeId}`, {
        parsedData: resumeData.parsedData,
      });

      if (response.success) {
        setResumeData(response.data);
        setHasChanges(false);
        message.success("Resume updated successfully!");
      } else {
        throw new Error("Failed to save changes");
      }
    } catch (err) {
      console.error("Save error", err.message || err);
      message.error("Failed to save changes. Please try again.");
    }
  };

  // Helper function for auto-saving changes silently
  // operationType: "manual_edit" (default) or "add_section" - kept for tracking purposes only
  // NO CREDIT CHECKS - Manual edits and adding sections are now FREE
  const autoSaveChanges = async (updatedParsedData, operationType = "manual_edit") => {
    try {
      const response = await fetch.put(`/resume/${resumeId}`, {
        parsedData: updatedParsedData,
        operationType: operationType,
      });

      if (response.success) {
        setHasChanges(false);
        return true;
      } else {
        logger.error("Failed to auto-save changes", {
          extras: {
            response,
            context: "modal_based_resume_auto_save"
          }
        });
        return false;
      }
    } catch (err) {

      logger.error("Auto-save error", {
        extras: {
          error: err.message || err,
          context: "modal_based_resume_auto_save_error"
        }
      });
      // Don't show error to user as it's auto-save, they can still manually save
      return false;
    }
  };

  // Section Title Change Handler
  const handleSectionTitleChange = async (sectionKey, newTitle) => {
    // Update local state
    setSectionTitles(prev => ({
      ...prev,
      [sectionKey]: newTitle
    }));

    // Update resumeData to include sectionTitles
    const updatedData = { ...resumeData };
    if (!updatedData.parsedData.sectionTitles) {
      updatedData.parsedData.sectionTitles = {};
    }
    updatedData.parsedData.sectionTitles[sectionKey] = newTitle;
    setResumeData(updatedData);
    setHasChanges(true);

    // Auto-save to database
    await autoSaveChanges(updatedData.parsedData);
    message.success(`Section title updated to "${newTitle}"`);
  };

  /**
   * Credit-checked modal opener wrapper
   * Checks if user has enough credits before opening any edit modal
   * Shows error message if credits are exhausted
   */
  // No credit checks needed - manual edits and adding sections are FREE
  // This is now just a simple pass-through wrapper for consistency
  const withCreditCheck = (openModalFn, isAddSection = false) => {
    return (...args) => {
      openModalFn(...args);
    };
  };

  // Personal Info Modal Handlers
  const openPersonalInfoModal = () => {
    const personalInfo = resumeData.parsedData.personalInfo || {};
    personalInfoForm.setFieldsValue({
      name: personalInfo.name || '',
      email: personalInfo.email || '',
      phone: personalInfo.phone || '',
      location: personalInfo.location || '',
      linkedin: personalInfo.linkedIn || '',
      github: personalInfo.github || '',
    });
    setPersonalInfoModal(true);
  };

  const handlePersonalInfoSave = async () => {
    try {
      const values = await personalInfoForm.validateFields();
      const updatedData = { ...resumeData };
      updatedData.parsedData.personalInfo = {
        ...updatedData.parsedData.personalInfo,
        ...values,
        linkedIn: values.linkedin, // Map lowercase to camelCase for consistency
      };
      setResumeData(updatedData);
      setHasChanges(true);
      setPersonalInfoModal(false);
      message.success('Personal information updated!');

      // Auto-save changes to database
      await autoSaveChanges(updatedData.parsedData);
    } catch (error) {
      logger.error('Validation failed', {
        extras: {
          error: error.message || error,
          context: "personal_information_validation"
        }
      });
    }
  };

  // Summary Modal Handlers
  const openSummaryModal = () => {
    const summary = resumeData.parsedData.summary || '';
    const summaryText = typeof summary === 'string' ? summary : summary.content || '';
    summaryForm.setFieldsValue({
      summary: summaryText,
    });
    setSummaryModal(true);
  };

  const handleSummarySave = async () => {
    try {
      const values = await summaryForm.validateFields();
      const updatedData = { ...resumeData };
      
      // Preserve AI Analysis if it exists
      const existingSummary = updatedData.parsedData.summary;
      if (typeof existingSummary === 'object' && existingSummary.aiAnalysis) {
        updatedData.parsedData.summary = {
          content: values.summary,
          aiAnalysis: existingSummary.aiAnalysis
        };
      } else {
        updatedData.parsedData.summary = values.summary;
      }
      
      setResumeData(updatedData);
      setHasChanges(true);
      setSummaryModal(false);
      message.success('Summary updated!');

      // Auto-save changes to database
      await autoSaveChanges(updatedData.parsedData);
    } catch (error) {
      logger.error('Validation failed', {
        extras: {
          error: error.message || error,
          context: "summary_validation"
        }
      });
    }
  };

  // Experience Modal Handlers
  const openExperienceModal = (experience = null, index = -1) => {
    setEditingExperience(index);
    setTempExperienceAIAnalysis(null); // Clear any previous temporary AI analysis
    // Helper function to parse dates safely
    const parseDate = (dateStr) => {
      if (!dateStr || dateStr === 'Present') return null;
      
      try {
        // Try different date formats
        const formats = ['MMM YYYY', 'MMMM YYYY', 'MM/YYYY', 'YYYY-MM', 'YYYY'];
        for (const format of formats) {
          const parsed = dayjs(dateStr, format, true);
          if (parsed.isValid()) {
            return parsed;
          }
        }
        
        // If no format matches, try parsing as-is
        const parsed = dayjs(dateStr);
        return parsed.isValid() ? parsed : null;
      } catch (error) {
        console.warn('Invalid date format:', dateStr);
        return null;
      }
    };
    
    const currentJob = experience?.endDate === 'Present' || experience?.current === true;
    
    experienceForm.setFieldsValue({
      title: experience?.title || '',
      company: experience?.company || '',
      location: experience?.location || '',
      startDate: parseDate(experience?.startDate),
      endDate: parseDate(experience?.endDate),
      current: currentJob,
      description: experience?.description || '',
    });
    
    setIsCurrentJob(currentJob);
    setExperienceModal(true);
  };

  const handleExperienceSave = async () => {
    try {
      const values = await experienceForm.validateFields();
      const updatedData = { ...resumeData };
      
      // Format dates safely using our utility
      const formatDate = (dateObj) => {
        if (!dateObj) return '';
        try {
          if (dayjs.isDayjs(dateObj) && dateObj.isValid()) {
            return formatDateToMMYYYY(dateObj.format('YYYY-MM-DD'));
          }
          return '';
        } catch (error) {
          console.warn('Invalid date object:', dateObj);
          return '';
        }
      };

      const experienceData = {
        title: values.title,
        company: values.company,
        location: values.location || '',
        startDate: formatDate(values.startDate),
        endDate: values.current ? 'Present' : formatDate(values.endDate),
        description: values.description,
        current: values.current || false,
      };

      if (!updatedData.parsedData.experience) {
        updatedData.parsedData.experience = [];
      }

      if (editingExperience === -1) {
        // Adding new experience - include AI analysis if available
        if (tempExperienceAIAnalysis) {
          experienceData.aiAnalysis = tempExperienceAIAnalysis;
        }
        updatedData.parsedData.experience.push(experienceData);
        updatedData.parsedData.experience = sortExperienceByDate(updatedData.parsedData.experience);
      } else {
        // Updating existing experience - preserve AI Analysis if it exists
        const existingExperience = updatedData.parsedData.experience[editingExperience];
        if (existingExperience && existingExperience.aiAnalysis) {
          experienceData.aiAnalysis = existingExperience.aiAnalysis;
        }
        updatedData.parsedData.experience[editingExperience] = experienceData;
        updatedData.parsedData.experience = sortExperienceByDate(updatedData.parsedData.experience);
      }

      setResumeData(updatedData);
      setHasChanges(true);
      setExperienceModal(false);
      setEditingExperience(null);
      setIsCurrentJob(false); // Reset state when modal closes
      setTempExperienceAIAnalysis(null); // Clear temporary AI analysis
      message.success('Experience updated!');

      // Auto-save changes to database
      await autoSaveChanges(updatedData.parsedData);
    } catch (error) {
      logger.error('Validation failed', {
        extras: {
          error: error.message || error,
          context: "experience_validation"
        }
      });
    }
  };

  const deleteExperience = (index) => {
    const experienceItem = resumeData?.parsedData?.experience?.[index];
    const itemName = experienceItem?.title || 'this experience';
    
    showDeleteConfirmation({
      title: "Delete Work Experience",
      message: "Are you sure you want to delete this work experience?",
      itemName,
      onConfirm: async () => {
        const updatedData = { ...resumeData };
        updatedData.parsedData.experience.splice(index, 1);
        setResumeData(updatedData);
        setHasChanges(true);
        message.success('Experience deleted!');

        // Auto-save changes to database
        await autoSaveChanges(updatedData.parsedData);
      }
    });
  };

  // Inline AI Enhancement Handler
  const handleInlineEnhance = async (e, section, sectionData, index) => {
    e.preventDefault();
    if (!inlinePrompt.trim()) return;
    
    try {
      const sectionKey = index !== null ? `${section}-${index}` : section;
      setEnhancingSection(sectionKey);
      setAiEnhanceLoading(true);
      
      // Get current section data
      let currentData = {};
      if (section === 'summary') {
        const summary = resumeData.parsedData.summary || '';
        currentData = typeof summary === 'string' ? { content: summary } : summary;
      } else if (section === 'personalInfo') {
        currentData = resumeData.parsedData.personalInfo || {};
      } else if (section === 'experience') {
        currentData = resumeData.parsedData.experience?.[index] || {};
      } else if (section === 'education') {
        currentData = resumeData.parsedData.education?.[index] || {};
      } else if (section === 'skills') {
        currentData = resumeData.parsedData.skills || {};
      } else if (section === 'projects') {
        currentData = resumeData.parsedData.projects?.[index] || {};
      }

      // For new items (index === -1), pass 0 as sectionIndex to avoid validation error
      const validIndex = (index === -1 || index === null) ? 0 : index;

      // Call AI enhancement API directly
      const response = await fetch.post('/enhance-section', {
        resumeId,
        sectionType: section,
        sectionIndex: validIndex,
        userRequest: inlinePrompt.trim(),
        currentData: currentData
      });

      if (response.data && response.data.enhancedContent) {
        
        // Apply the enhanced content directly to the database using apply-enhancement
        const applyResponse = await fetch.post('/apply-enhancement', {
          enhancementId: response.data.enhancementId,
          resumeId
        });

        if (applyResponse.success) {
          // Refresh resume data to get the updated content
          await fetchResumeData();
          message.success('AI enhancement applied successfully!');
          
          // Reset inline input
          setActiveInlineInput(null);
          setInlinePrompt("");
        } else {
          throw new Error('Failed to apply enhancement');
        }
      } else {
        message.error('Failed to generate AI enhancement');
      }
    } catch (error) {
      logger.error('AI enhancement error', {
        extras: {
          error: error.message || error,
          context: "inline_ai_enhancement_error"
        }
      });
      message.error('Error generating AI enhancement: ' + (error.response?.data?.message || error.message));
    } finally {
      setAiEnhanceLoading(false);
      setEnhancingSection(null);
    }
  };

  // Form-specific AI Enhancement handler
  const handleFormEnhance = async (section, fieldName, index) => {
    let prompt = '';
    let setPrompt = null;
    
    if (section === 'summary') {
      prompt = summaryEnhancePrompt;
      setPrompt = setSummaryEnhancePrompt;
    } else if (section === 'experience') {
      prompt = experienceEnhancePrompt;
      setPrompt = setExperienceEnhancePrompt;
    } else if (section === 'projects') {
      prompt = projectEnhancePrompt;
      setPrompt = setProjectEnhancePrompt;
    }
    
    if (!prompt.trim()) return;
    
    try {
      setEnhancingSection(section);
      setAiEnhanceLoading(true);
      
      // Get current data from form
      let currentData = {};
      if (section === 'summary') {
        const summaryValue = summaryForm.getFieldValue('summary') || '';
        currentData = { content: summaryValue };
      } else if (section === 'experience') {
        currentData = {
          title: experienceForm.getFieldValue('title') || '',
          company: experienceForm.getFieldValue('company') || '',
          description: experienceForm.getFieldValue('description') || ''
        };
      } else if (section === 'projects') {
        const techValue = projectForm.getFieldValue('technologies') || '';
        currentData = {
          name: projectForm.getFieldValue('name') || '',
          description: projectForm.getFieldValue('description') || '',
          technologies: techValue ? techValue.split(',').map(t => t.trim()) : []
        };
      }

      // For new items (index === -1), pass 0 as sectionIndex to avoid validation error
      const validIndex = (index === -1 || index === null) ? 0 : index;

      // Call AI enhancement API
      const response = await fetch.post('/enhance-section', {
        resumeId,
        sectionType: section,
        sectionIndex: validIndex,
        userRequest: prompt.trim(),
        currentData: currentData
      });

      if (response.data && response.data.enhancedContent) {
        // Apply enhancement directly to form
        if (section === 'summary') {
          summaryForm.setFieldsValue({
            summary: response.data.enhancedContent
          });
        } else if (section === 'experience') {
          experienceForm.setFieldsValue({
            description: response.data.enhancedContent
          });
          // Store AI analysis for new experience entries
          if (index === -1 && response.data.aiAnalysis) {
            setTempExperienceAIAnalysis(response.data.aiAnalysis);
          }
        } else if (section === 'projects') {
          const enhanced = response.data.enhancedContent;
          projectForm.setFieldsValue({
            description: enhanced.description,
            technologies: enhanced.technologies ? enhanced.technologies.join(', ') : projectForm.getFieldValue('technologies')
          });
          // Store AI analysis for new project entries
          if (index === -1 && response.data.aiAnalysis) {
            setTempProjectAIAnalysis(response.data.aiAnalysis);
          }
        }
        
        message.success('AI enhancement applied to form!');
        
        // Clear the prompt
        if (setPrompt) setPrompt('');
      } else {
        message.error('Failed to generate AI enhancement');
      }
    } catch (error) {
      logger.error('Form AI enhancement error', {
        extras: {
          error: error.message || error,
          context: "form_ai_enhancement_error"
        }
      });
      message.error('Error generating AI enhancement: ' + (error.response?.data?.message || error.message));
    } finally {
      setAiEnhanceLoading(false);
      setEnhancingSection(null);
    }
  };

  // Education Modal Handlers
  const openEducationModal = (education = null, index = -1) => {
    setEditingEducation(index);
    
    // Helper function to parse dates safely (same as experience)
    const parseDate = (dateStr) => {
      if (!dateStr || dateStr === 'Present') return null;
      
      try {
        // Try different date formats
        const formats = ['MMM YYYY', 'MMMM YYYY', 'MM/YYYY', 'YYYY-MM', 'YYYY'];
        for (const format of formats) {
          const parsed = dayjs(dateStr, format, true);
          if (parsed.isValid()) {
            return parsed;
          }
        }
        
        // If no format matches, try parsing as-is
        const parsed = dayjs(dateStr);
        return parsed.isValid() ? parsed : null;
      } catch (error) {
        console.warn('Invalid date format:', dateStr);
        return null;
      }
    };

    educationForm.setFieldsValue({
      degree: education?.degree || '',
      school: education?.school || '',
      startDate: parseDate(education?.startDate),
      endDate: parseDate(education?.endDate) || parseDate(education?.graduationDate),
      location: education?.location || '',
      gpa: education?.gpa || '',
    });
    setEducationModal(true);
  };

  const handleEducationSave = async () => {
    try {
      const values = await educationForm.validateFields();
      const updatedData = { ...resumeData };
      
      // Helper function to format dates safely using our utility
      const formatDate = (date) => {
        if (!date) return '';
        try {
          if (dayjs.isDayjs(date) && date.isValid()) {
            return formatDateToMMYYYY(date.format('YYYY-MM-DD'));
          }
          return '';
        } catch (error) {
          console.warn('Invalid date format:', date);
          return '';
        }
      };

      // Helper function to format year safely
      const formatYear = (date) => {
        if (!date) return '';
        try {
          if (dayjs.isDayjs(date) && date.isValid()) {
            return date.format('YYYY');
          }
          return '';
        } catch (error) {
          console.warn('Invalid date format:', date);
          return '';
        }
      };
      
      const educationData = {
        degree: values.degree,
        school: values.school,
        startDate: formatDate(values.startDate),
        endDate: formatDate(values.endDate),
        location: values.location || '',
        gpa: values.gpa || '',
        // For backward compatibility, also set graduationDate
        graduationDate: formatYear(values.endDate)
      };

      if (!updatedData.parsedData.education) {
        updatedData.parsedData.education = [];
      }

      if (editingEducation === -1) {
        // Adding new education - add to array first, then sort chronologically
        updatedData.parsedData.education.push(educationData);
        updatedData.parsedData.education = sortEducationByDate(updatedData.parsedData.education);
      } else {
        // Updating existing education - update and re-sort to maintain chronological order
        updatedData.parsedData.education[editingEducation] = educationData;
        updatedData.parsedData.education = sortEducationByDate(updatedData.parsedData.education);
      }

      setResumeData(updatedData);
      setHasChanges(true);
      setEducationModal(false);
      setEditingEducation(null);
      message.success('Education updated!');

      // Auto-save changes to database
      await autoSaveChanges(updatedData.parsedData);
    } catch (error) {
      logger.error('Validation failed', {
        extras: {
          error: error.message || error,
          context: "education_validation"
        }
      });
    }
  };

  const deleteEducation = (index) => {
    const educationItem = resumeData?.parsedData?.education?.[index];
    const itemName = educationItem?.degree || 'this education';
    
    showDeleteConfirmation({
      title: "Delete Education",
      message: "Are you sure you want to delete this education entry?",
      itemName,
      onConfirm: async () => {
        const updatedData = { ...resumeData };
        updatedData.parsedData.education.splice(index, 1);
        setResumeData(updatedData);
        setHasChanges(true);
        
        // Auto-save to database
        await autoSaveChanges(updatedData.parsedData);
        
        message.success('Education deleted!');
      }
    });
  };

  // Skills Modal Handlers
  const openSkillsModal = () => {
    const skills = resumeData.parsedData.skills || {};

    if (skills.categorized) {
      // Load categorized skills
      setTempCategorizedSkills({
        technical: [...(skills.categorized.technical || [])],
        soft: [...(skills.categorized.soft || [])],
        tools: [...(skills.categorized.tools || [])],
        other: [...(skills.categorized.other || [])]
      });
    } else {
      // Load flat skills list
      const skillsList = Array.isArray(skills) ? skills : skills.list || [];
      setTempSkills([...skillsList]);
    }

    setSkillInput('');
    setCategoryInput('');
    setSelectedCategory('technical');
    setSkillsModal(true);
  };

  const addSkill = () => {
    if (skillInput.trim() && !tempSkills.includes(skillInput.trim())) {
      setTempSkills([...tempSkills, skillInput.trim()]);
      setSkillInput('');
    }
  };

  const removeSkill = (skillToRemove) => {
    setTempSkills(tempSkills.filter(skill => skill !== skillToRemove));
  };

  // Categorized skills handlers
  const addCategorizedSkill = () => {
    if (categoryInput.trim() && !tempCategorizedSkills[selectedCategory].includes(categoryInput.trim())) {
      setTempCategorizedSkills({
        ...tempCategorizedSkills,
        [selectedCategory]: [...tempCategorizedSkills[selectedCategory], categoryInput.trim()]
      });
      setCategoryInput('');
    }
  };

  const removeCategorizedSkill = (category, skillToRemove) => {
    setTempCategorizedSkills({
      ...tempCategorizedSkills,
      [category]: tempCategorizedSkills[category].filter(skill => skill !== skillToRemove)
    });
  };

  const clearAllSkills = () => {
    Modal.confirm({
      title: "Clear All Skills",
      content: "Are you sure you want to remove all skills? This action cannot be undone.",
      okText: "Delete All",
      cancelText: "Cancel",
      okType: "danger",
      onOk: () => {
        setTempSkills([]);
        message.success('All skills cleared!');
      }
    });
  };

  const handleSkillsSave = async () => {
    const updatedData = { ...resumeData };
    const currentSkills = updatedData.parsedData.skills || {};

    if (currentSkills.categorized) {
      // Save categorized skills format
      updatedData.parsedData.skills = {
        ...currentSkills,
        categorized: tempCategorizedSkills,
        // Keep existing AI analysis if it exists
        aiAnalysis: currentSkills.aiAnalysis
      };
    } else {
      // Save flat skills list for backward compatibility
      updatedData.parsedData.skills = Array.isArray(currentSkills)
        ? tempSkills
        : { ...currentSkills, list: tempSkills };
    }

    setResumeData(updatedData);
    setHasChanges(true);
    setSkillsModal(false);
    message.success('Skills updated!');

    // Auto-save changes to database
    await autoSaveChanges(updatedData.parsedData);
  };

  // Project Modal Handlers
  const openProjectModal = (project = null, index = -1) => {
    setEditingProject(index);
    setTempProjectAIAnalysis(null); // Clear any previous temporary AI analysis
    projectForm.setFieldsValue({
      name: project?.name || project?.title || '',
      description: project?.description || '',
      technologies: project?.technologies?.join(', ') || '',
      link: project?.link || '',
    });
    setProjectModal(true);
  };

  const handleProjectSave = async () => {
    try {
      const values = await projectForm.validateFields();
      const updatedData = { ...resumeData };
      
      const projectData = {
        name: values.name,
        title: values.name, // Some resumes use title instead of name
        description: values.description,
        technologies: values.technologies ? values.technologies.split(',').map(tech => tech.trim()) : [],
        link: values.link,
      };

      if (!updatedData.parsedData.projects) {
        updatedData.parsedData.projects = [];
      }

      if (editingProject === -1) {
        // Adding new project - include AI analysis if available
        if (tempProjectAIAnalysis) {
          projectData.aiAnalysis = tempProjectAIAnalysis;
        }
        updatedData.parsedData.projects.push(projectData);
      } else {
        // Updating existing project - preserve AI Analysis if it exists
        const existingProject = updatedData.parsedData.projects[editingProject];
        if (existingProject && existingProject.aiAnalysis) {
          projectData.aiAnalysis = existingProject.aiAnalysis;
        }
        updatedData.parsedData.projects[editingProject] = projectData;
      }

      setResumeData(updatedData);
      setHasChanges(true);
      setProjectModal(false);
      setEditingProject(null);
      setTempProjectAIAnalysis(null); // Clear temporary AI analysis
      message.success('Project updated!');

      // Auto-save changes to database
      await autoSaveChanges(updatedData.parsedData);
    } catch (error) {
      logger.error('Validation failed', {
        extras: {
          error: error.message || error,
          context: "project_validation"
        }
      });
    }
  };

  const deleteProject = (index) => {
    const projectItem = resumeData?.parsedData?.projects?.[index];
    const itemName = projectItem?.name || projectItem?.title || 'this project';
    
    showDeleteConfirmation({
      title: "Delete Project",
      message: "Are you sure you want to delete this project?",
      itemName,
      onConfirm: async () => {
        const updatedData = { ...resumeData };
        updatedData.parsedData.projects.splice(index, 1);
        setResumeData(updatedData);
        setHasChanges(true);
        message.success('Project deleted!');

        // Auto-save changes to database
        await autoSaveChanges(updatedData.parsedData);
      }
    });
  };

  // Handle reordering items within a section (drag and drop)
  const handleReorderItems = async (sectionName, reorderedItems) => {
    try {
      const updatedData = { ...resumeData };
      updatedData.parsedData[sectionName] = reorderedItems;
      setResumeData(updatedData);
      setHasChanges(true);

      // Auto-save changes to database
      await autoSaveChanges(updatedData.parsedData);
    } catch (error) {
      console.error("Failed to reorder items:", error);
      message.error('Failed to reorder items');
    }
  };

  // Certification Modal Handlers
  const openCertificationModal = (certification = null, index = -1) => {
    setEditingCertification(index);
    certificationForm.setFieldsValue({
      name: certification?.name || '',
      issuer: certification?.issuer || '',
      date: certification?.date ? (() => {
        try {
          // Try multiple formats for parsing existing dates
          const dateStr = certification.date;
          let parsedDate = dayjs(dateStr, 'MMM YYYY', true);
          
          if (!parsedDate.isValid()) {
            parsedDate = dayjs(dateStr, 'MMMM YYYY', true);
          }
          if (!parsedDate.isValid()) {
            parsedDate = dayjs(dateStr, 'MM/YYYY', true);
          }
          if (!parsedDate.isValid()) {
            parsedDate = dayjs(dateStr);
          }
          
          return parsedDate.isValid() ? parsedDate : null;
        } catch (error) {
          logger.error('Error parsing certification date', {
            extras: {
              error: error.message || error,
              context: "certification_date_parsing"
            }
          });
          return null;
        }
      })() : null,
    });
    setCertificationModal(true);
  };

  const handleCertificationSave = async () => {
    try {
      const values = await certificationForm.validateFields();
      const updatedData = { ...resumeData };
      
      const certificationData = {
        name: values.name,
        issuer: values.issuer,
        date: values.date ? values.date.format('MMM YYYY') : '',
      };

      if (!updatedData.parsedData.certifications) {
        updatedData.parsedData.certifications = [];
      }

      if (editingCertification === -1) {
        // Adding new certification
        updatedData.parsedData.certifications.push(certificationData);
      } else {
        // Updating existing certification
        updatedData.parsedData.certifications[editingCertification] = certificationData;
      }

      setResumeData(updatedData);
      setHasChanges(true);
      setCertificationModal(false);
      setEditingCertification(null);
      
      // Auto-save to database
      await autoSaveChanges(updatedData.parsedData);

      message.success('Certification updated!');
    } catch (error) {
      logger.error('Validation failed', {
        extras: {
          error: error.message || error,
          context: "certification_validation"
        }
      });
    }
  };

  const deleteCertification = (index) => {
    const certificationItem = resumeData?.parsedData?.certifications?.[index];
    const itemName = certificationItem?.name || 'this certification';
    
    showDeleteConfirmation({
      title: "Delete Certification",
      message: "Are you sure you want to delete this certification?",
      itemName,
      onConfirm: async () => {
        const updatedData = { ...resumeData };
        updatedData.parsedData.certifications.splice(index, 1);
        setResumeData(updatedData);
        setHasChanges(true);
        
        // Auto-save to database
        await autoSaveChanges(updatedData.parsedData);
        
        message.success('Certification deleted!');
      }
    });
  };

  // Achievements Modal Handlers
  const openAchievementsModal = () => {
    // Initialize temp achievements from current data
    const currentAchievements = resumeData?.parsedData?.achievements || [];
    setTempAchievements(currentAchievements.map(achievement => ({
      description: achievement.description || ''
    })));
    setAchievementsModal(true);
  };

  const addAchievement = () => {
    setTempAchievements([...tempAchievements, { description: '' }]);
  };

  const updateAchievement = (index, field, value) => {
    const updated = [...tempAchievements];
    updated[index] = { ...updated[index], [field]: value };
    setTempAchievements(updated);
  };

  const removeAchievement = (index) => {
    const updated = tempAchievements.filter((_, i) => i !== index);
    setTempAchievements(updated);
  };

  const handleAchievementsSave = async () => {
    try {
      const updatedData = { ...resumeData };

      // Filter out empty achievements
      const validAchievements = tempAchievements.filter(achievement =>
        achievement.description && achievement.description.trim()
      );

      // If no valid achievements, show warning and don't save
      if (validAchievements.length === 0) {
        message.warning('Please add at least one achievement before saving');
        return;
      }

      // Check if this is adding a new section
      const isNewSection = !visibleSections.includes('achievements');

      updatedData.parsedData.achievements = validAchievements;

      // Add to visibleSections since we have valid data
      if (isNewSection) {
        setVisibleSections([...visibleSections, 'achievements']);
      }

      setResumeData(updatedData);
      setHasChanges(true);
      setAchievementsModal(false);

      // Auto-save to database with appropriate operation type
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");

      message.success(isNewSection ? 'Achievements section added!' : 'Achievements updated!');
    } catch (error) {
      logger.error('Error saving achievements', {
        extras: {
          error: error.message || error,
          context: "achievements_saving_error"
        }
      });
      message.error('Failed to save achievements');
    }
  };

  // Languages Modal Handlers
  const openLanguagesModal = () => {
    const currentLanguages = resumeData?.parsedData?.languages || [];
    setTempLanguages(currentLanguages.map(lang => ({
      name: lang.name || '',
      proficiency: lang.proficiency || ''
    })));
    setLanguagesModal(true);
  };

  const addLanguage = () => {
    setTempLanguages([...tempLanguages, { name: '', proficiency: '' }]);
  };

  const updateLanguage = (index, field, value) => {
    const updated = [...tempLanguages];
    updated[index] = { ...updated[index], [field]: value };
    setTempLanguages(updated);
  };

  const removeLanguage = (index) => {
    const updated = tempLanguages.filter((_, i) => i !== index);
    setTempLanguages(updated);
  };

  const handleLanguagesSave = async () => {
    try {
      const updatedData = { ...resumeData };

      // Filter out empty languages
      const validLanguages = tempLanguages.filter(lang =>
        lang.name && lang.name.trim()
      );

      // If no valid languages, show warning and don't save
      if (validLanguages.length === 0) {
        message.warning('Please add at least one language before saving');
        return;
      }

      // Check if this is adding a new section
      const isNewSection = !visibleSections.includes('languages');

      updatedData.parsedData.languages = validLanguages;

      // Add to visibleSections since we have valid data
      if (isNewSection) {
        setVisibleSections([...visibleSections, 'languages']);
      }

      setResumeData(updatedData);
      setHasChanges(true);
      setLanguagesModal(false);

      // Auto-save to database with appropriate operation type
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");

      message.success(isNewSection ? 'Languages section added!' : 'Languages updated!');
    } catch (error) {
      logger.error('Error saving languages', {
        extras: {
          error: error.message || error,
          context: "languages_saving_error"
        }
      });
      message.error('Failed to save languages');
    }
  };

  // Awards Modal Handlers
  const openAwardsModal = () => {
    const currentAwards = resumeData?.parsedData?.awards || [];
    setTempAwards(currentAwards.map(award => ({
      title: award.title || '',
      issuer: award.issuer || '',
      date: award.date || '',
      description: award.description || ''
    })));
    setAwardsModal(true);
  };

  const addAward = () => {
    setTempAwards([...tempAwards, { title: '', issuer: '', date: '', description: '' }]);
  };

  const updateAward = (index, field, value) => {
    const updated = [...tempAwards];
    updated[index] = { ...updated[index], [field]: value };
    setTempAwards(updated);
  };

  const removeAward = (index) => {
    const updated = tempAwards.filter((_, i) => i !== index);
    setTempAwards(updated);
  };

  const handleAwardsSave = async () => {
    try {
      const updatedData = { ...resumeData };

      // Filter out empty awards
      const validAwards = tempAwards.filter(award =>
        award.title && award.title.trim()
      );

      // If no valid awards, show warning and don't save
      if (validAwards.length === 0) {
        message.warning('Please add at least one award before saving');
        return;
      }

      // Check if this is adding a new section
      const isNewSection = !visibleSections.includes('awards');

      updatedData.parsedData.awards = validAwards;

      // Add to visibleSections since we have valid data
      if (isNewSection) {
        setVisibleSections([...visibleSections, 'awards']);
      }

      setResumeData(updatedData);
      setHasChanges(true);
      setAwardsModal(false);

      // Auto-save to database with appropriate operation type
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");

      message.success(isNewSection ? 'Awards section added!' : 'Awards updated!');
    } catch (error) {
      logger.error('Error saving awards', {
        extras: {
          error: error.message || error,
          context: "awards_saving_error"
        }
      });
      message.error('Failed to save awards');
    }
  };

  // ============ VOLUNTEERING SECTION HANDLERS ============
  const openVolunteeringModal = () => {
    const volunteering = resumeData?.parsedData?.volunteering || [];
    setTempVolunteering(volunteering.map(vol => ({
      organization: vol.organization || '',
      role: vol.role || '',
      location: vol.location || '',
      startDate: vol.startDate || '',
      endDate: vol.endDate || '',
      description: vol.description || '',
      isCurrentRole: vol.isCurrentRole || false,
    })));
    setVolunteeringModal(true);
  };

  const addVolunteering = () => {
    setTempVolunteering([...tempVolunteering, { organization: '', role: '', location: '', startDate: '', endDate: '', description: '', isCurrentRole: false }]);
  };

  const updateVolunteering = (index, field, value) => {
    const updated = [...tempVolunteering];
    updated[index] = { ...updated[index], [field]: value };
    setTempVolunteering(updated);
  };

  const removeVolunteering = (index) => {
    const updated = tempVolunteering.filter((_, i) => i !== index);
    setTempVolunteering(updated);
  };

  const handleVolunteeringSave = async () => {
    try {
      const updatedData = { ...resumeData };
      const validVolunteering = tempVolunteering.filter(vol => vol.organization && vol.organization.trim());
      if (validVolunteering.length === 0) {
        message.warning('Please add at least one volunteering experience before saving');
        return;
      }
      // Check if this is adding a new section
      const isNewSection = !visibleSections.includes('volunteering');
      updatedData.parsedData.volunteering = validVolunteering;
      if (isNewSection) {
        setVisibleSections([...visibleSections, 'volunteering']);
      }
      setResumeData(updatedData);
      setHasChanges(true);
      setVolunteeringModal(false);
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");
      message.success(isNewSection ? 'Volunteering section added!' : 'Volunteering updated!');
    } catch (error) {
      logger.error('Error saving volunteering', { extras: { error: error.message || error, context: "volunteering_saving_error" } });
      message.error('Failed to save volunteering');
    }
  };

  // ============ INTERESTS SECTION HANDLERS ============
  const openInterestsModal = () => {
    const interests = resumeData?.parsedData?.interests || [];
    setTempInterests(interests.map(interest => ({
      name: interest.name || '',
      description: interest.description || '',
    })));
    setInterestsModal(true);
  };

  const addInterest = () => {
    setTempInterests([...tempInterests, { name: '', description: '' }]);
  };

  const updateInterest = (index, field, value) => {
    const updated = [...tempInterests];
    updated[index] = { ...updated[index], [field]: value };
    setTempInterests(updated);
  };

  const removeInterest = (index) => {
    const updated = tempInterests.filter((_, i) => i !== index);
    setTempInterests(updated);
  };

  const handleInterestsSave = async () => {
    try {
      const updatedData = { ...resumeData };
      const validInterests = tempInterests.filter(interest => interest.name && interest.name.trim());
      if (validInterests.length === 0) {
        message.warning('Please add at least one interest before saving');
        return;
      }
      // Check if this is adding a new section
      const isNewSection = !visibleSections.includes('interests');
      updatedData.parsedData.interests = validInterests;
      if (isNewSection) {
        setVisibleSections([...visibleSections, 'interests']);
      }
      setResumeData(updatedData);
      setHasChanges(true);
      setInterestsModal(false);
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");
      message.success(isNewSection ? 'Interests section added!' : 'Interests updated!');
    } catch (error) {
      logger.error('Error saving interests', { extras: { error: error.message || error, context: "interests_saving_error" } });
      message.error('Failed to save interests');
    }
  };

  // ============ INDUSTRY EXPERTISE SECTION HANDLERS ============
  const openIndustryExpertiseModal = () => {
    const expertise = resumeData?.parsedData?.industryExpertise || [];
    setTempIndustryExpertise(expertise.map(exp => ({
      name: exp.name || '',
      proficiency: exp.proficiency || '',
      yearsOfExperience: exp.yearsOfExperience || '',
    })));
    setIndustryExpertiseModal(true);
  };

  const addIndustryExpertise = () => {
    setTempIndustryExpertise([...tempIndustryExpertise, { name: '', proficiency: '', yearsOfExperience: '' }]);
  };

  const updateIndustryExpertise = (index, field, value) => {
    const updated = [...tempIndustryExpertise];
    updated[index] = { ...updated[index], [field]: value };
    setTempIndustryExpertise(updated);
  };

  const removeIndustryExpertise = (index) => {
    const updated = tempIndustryExpertise.filter((_, i) => i !== index);
    setTempIndustryExpertise(updated);
  };

  const handleIndustryExpertiseSave = async () => {
    try {
      const updatedData = { ...resumeData };
      const validExpertise = tempIndustryExpertise.filter(exp => exp.name && exp.name.trim());
      if (validExpertise.length === 0) {
        message.warning('Please add at least one expertise before saving');
        return;
      }
      // Check if this is adding a new section
      const isNewSection = !visibleSections.includes('industryExpertise');
      updatedData.parsedData.industryExpertise = validExpertise;
      if (isNewSection) {
        setVisibleSections([...visibleSections, 'industryExpertise']);
      }
      setResumeData(updatedData);
      setHasChanges(true);
      setIndustryExpertiseModal(false);
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");
      message.success(isNewSection ? 'Industry expertise section added!' : 'Industry expertise updated!');
    } catch (error) {
      logger.error('Error saving industry expertise', { extras: { error: error.message || error, context: "industry_expertise_saving_error" } });
      message.error('Failed to save industry expertise');
    }
  };

  // ============ PUBLICATIONS SECTION HANDLERS ============
  const openPublicationsModal = () => {
    const publications = resumeData?.parsedData?.publications || [];
    setTempPublications(publications.map(pub => ({
      title: pub.title || '',
      authors: pub.authors || [],
      journal: pub.journal || '',
      date: pub.date || '',
      link: pub.link || pub.url || '',
      description: pub.description || '',
    })));
    setPublicationsModal(true);
  };

  const addPublication = () => {
    setTempPublications([...tempPublications, { title: '', authors: [], journal: '', date: '', link: '', description: '' }]);
  };

  const updatePublication = (index, field, value) => {
    const updated = [...tempPublications];
    updated[index] = { ...updated[index], [field]: value };
    setTempPublications(updated);
  };

  const removePublication = (index) => {
    const updated = tempPublications.filter((_, i) => i !== index);
    setTempPublications(updated);
  };

  const handlePublicationsSave = async () => {
    try {
      const updatedData = { ...resumeData };
      const validPubs = tempPublications.filter(pub => pub.title && pub.title.trim());
      if (validPubs.length === 0) {
        message.warning('Please add at least one publication before saving');
        return;
      }
      // Check if this is adding a new section
      const isNewSection = !visibleSections.includes('publications');
      updatedData.parsedData.publications = validPubs;
      if (isNewSection) {
        setVisibleSections([...visibleSections, 'publications']);
      }
      setResumeData(updatedData);
      setHasChanges(true);
      setPublicationsModal(false);
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");
      message.success(isNewSection ? 'Publications section added!' : 'Publications updated!');
    } catch (error) {
      logger.error('Error saving publications', { extras: { error: error.message || error, context: "publications_saving_error" } });
      message.error('Failed to save publications');
    }
  };

  // ============ REFERENCES SECTION HANDLERS ============
  const openReferencesModal = () => {
    const references = resumeData?.parsedData?.references || [];
    setTempReferences(references.map(ref => ({
      name: ref.name || '',
      title: ref.title || '',
      company: ref.company || '',
      email: ref.email || '',
      phone: ref.phone || '',
      relationship: ref.relationship || '',
    })));
    setReferencesModal(true);
  };

  const addReference = () => {
    setTempReferences([...tempReferences, { name: '', title: '', company: '', email: '', phone: '', relationship: '' }]);
  };

  const updateReference = (index, field, value) => {
    const updated = [...tempReferences];
    updated[index] = { ...updated[index], [field]: value };
    setTempReferences(updated);
  };

  const removeReference = (index) => {
    const updated = tempReferences.filter((_, i) => i !== index);
    setTempReferences(updated);
  };

  const handleReferencesSave = async () => {
    try {
      const updatedData = { ...resumeData };
      const validRefs = tempReferences.filter(ref => ref.name && ref.name.trim());
      if (validRefs.length === 0) {
        message.warning('Please add at least one reference before saving');
        return;
      }
      // Check if this is adding a new section
      const isNewSection = !visibleSections.includes('references');
      updatedData.parsedData.references = validRefs;
      if (isNewSection) {
        setVisibleSections([...visibleSections, 'references']);
      }
      setResumeData(updatedData);
      setHasChanges(true);
      setReferencesModal(false);
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");
      message.success(isNewSection ? 'References section added!' : 'References updated!');
    } catch (error) {
      logger.error('Error saving references', { extras: { error: error.message || error, context: "references_saving_error" } });
      message.error('Failed to save references');
    }
  };

  // Custom Section Modal Handlers
  const openCustomSectionModal = () => {
    // Initialize with empty custom section for creating new section
    setTempCustomSection({ title: '', items: [{ content: '' }] });
    setEditingCustomSectionId(null); // Ensure we're in create mode
    setCustomSectionModal(true);
  };

  const updateCustomSectionTitle = (value) => {
    setTempCustomSection(prev => ({ ...prev, title: value }));
  };

  const addCustomSectionItem = () => {
    setTempCustomSection(prev => ({
      ...prev,
      items: [...prev.items, { content: '' }]
    }));
  };

  const updateCustomSectionItem = (index, value) => {
    setTempCustomSection(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index] = { content: value };
      return { ...prev, items: updatedItems };
    });
  };

  const removeCustomSectionItem = (index) => {
    setTempCustomSection(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Open modal to edit an existing custom section
  const openEditCustomSectionModal = (customSectionId) => {
    const customSections = resumeData?.parsedData?.customSections || [];
    const sectionToEdit = customSections.find(s => s.id === customSectionId);

    if (sectionToEdit) {
      setTempCustomSection({
        title: sectionToEdit.title || '',
        items: sectionToEdit.items?.length > 0
          ? sectionToEdit.items.map(item => ({ content: item.content || '' }))
          : [{ content: '' }]
      });
      setEditingCustomSectionId(customSectionId);
      setCustomSectionModal(true);
    }
  };

  // Save edited custom section (handles both create and edit)
  const handleCustomSectionUpdate = async () => {
    try {
      // Validate title
      if (!tempCustomSection.title || !tempCustomSection.title.trim()) {
        message.warning('Please enter a section title');
        return;
      }

      // Filter out empty items
      const validItems = tempCustomSection.items.filter(item =>
        item.content && item.content.trim()
      );

      // If no valid items, show warning
      if (validItems.length === 0) {
        message.warning('Please add at least one item to your custom section');
        return;
      }

      const updatedData = { ...resumeData };
      const existingCustomSections = updatedData.parsedData.customSections || [];

      if (editingCustomSectionId) {
        // Edit existing section
        const sectionIndex = existingCustomSections.findIndex(s => s.id === editingCustomSectionId);
        if (sectionIndex !== -1) {
          existingCustomSections[sectionIndex] = {
            ...existingCustomSections[sectionIndex],
            title: tempCustomSection.title.trim(),
            items: validItems
          };
          updatedData.parsedData.customSections = existingCustomSections;
        }
      } else {
        // Create new section
        const newCustomSection = {
          id: `custom_${Date.now()}`,
          title: tempCustomSection.title.trim(),
          items: validItems
        };
        updatedData.parsedData.customSections = [...existingCustomSections, newCustomSection];

        // Add custom section ID to visibleSections
        if (!visibleSections.includes(newCustomSection.id)) {
          setVisibleSections([...visibleSections, newCustomSection.id]);
        }
      }

      // Determine if this is a new section or an edit
      const isNewSection = !editingCustomSectionId;

      setResumeData(updatedData);
      setHasChanges(true);
      setCustomSectionModal(false);
      setEditingCustomSectionId(null);

      // Auto-save to database with appropriate operation type
      await autoSaveChanges(updatedData.parsedData, isNewSection ? "add_section" : "manual_edit");

      message.success(isNewSection ? 'Custom section added!' : 'Custom section updated!');
    } catch (error) {
      logger.error('Error saving custom section', {
        extras: {
          error: error.message || error,
          context: "custom_section_update_error"
        }
      });
      message.error('Failed to save custom section');
    }
  };

  // Delete a custom section
  const deleteCustomSection = async (customSectionId) => {
    try {
      const updatedData = { ...resumeData };
      const existingCustomSections = updatedData.parsedData.customSections || [];

      // Filter out the section to delete
      updatedData.parsedData.customSections = existingCustomSections.filter(s => s.id !== customSectionId);

      // Remove from visibleSections
      setVisibleSections(prev => prev.filter(id => id !== customSectionId));

      setResumeData(updatedData);
      setHasChanges(true);

      // Auto-save to database
      await autoSaveChanges(updatedData.parsedData);

      message.success('Custom section deleted!');
    } catch (error) {
      logger.error('Error deleting custom section', {
        extras: {
          error: error.message || error,
          context: "custom_section_delete_error"
        }
      });
      message.error('Failed to delete custom section');
    }
  };

  // Delete a standard section (achievements, languages, awards, etc.)
  const deleteSection = (sectionId) => {
    const sectionNames = {
      achievements: 'Achievements',
      languages: 'Languages',
      awards: 'Awards',
      volunteering: 'Volunteering',
      interests: 'Interests',
      industryExpertise: 'Industry Expertise',
      publications: 'Publications',
      references: 'References'
    };

    const sectionName = sectionNames[sectionId] || sectionId;

    showDeleteConfirmation({
      title: `Delete ${sectionName} Section`,
      message: `Are you sure you want to delete the entire ${sectionName} section? All data in this section will be removed.`,
      itemName: sectionName,
      onConfirm: async () => {
        try {
          const updatedData = { ...resumeData };

          // Clear the section data
          if (updatedData.parsedData[sectionId]) {
            updatedData.parsedData[sectionId] = [];
          }

          // Remove from visibleSections
          setVisibleSections(prev => prev.filter(id => id !== sectionId));

          setResumeData(updatedData);
          setHasChanges(true);

          // Auto-save to database
          await autoSaveChanges(updatedData.parsedData);

          message.success(`${sectionName} section deleted!`);
        } catch (error) {
          logger.error('Error deleting section', {
            extras: {
              error: error.message || error,
              sectionId,
              context: "section_delete_error"
            }
          });
          message.error(`Failed to delete ${sectionName} section`);
        }
      }
    });
  };

  // Handle section selection from AddSectionModal
  const handleSectionSelect = (sectionId) => {
    // Don't add to visibleSections here - only add after user saves data
    // This prevents empty sections from showing in the template

    // Open the appropriate modal for the selected section
    switch (sectionId) {
      case 'achievements':
        openAchievementsModal();
        break;
      case 'languages':
        openLanguagesModal();
        break;
      case 'awards':
        openAwardsModal();
        break;
      case 'publications':
        openPublicationsModal();
        break;
      case 'references':
        openReferencesModal();
        break;
      case 'volunteering':
        openVolunteeringModal();
        break;
      case 'interests':
        openInterestsModal();
        break;
      case 'industryExpertise':
        openIndustryExpertiseModal();
        break;
      case 'custom':
        openCustomSectionModal();
        break;
      default:
        message.info(`${sectionId} section added!`);
    }

    setAddSectionModal(false);
  };

  // Cover Letter Success Handler
  const handleCoverLetterSuccess = async (updatedResume) => {
    try {
      // Refresh resume data to get the updated cover letter
      await fetchResumeData();
      message.success('Cover letter saved successfully!');
    } catch (error) {
      logger.error('Error refreshing resume data after cover letter save', {
        extras: {
          error: error.message || error,
          context: "cover_letter_success_handler"
        }
      });
    }
  };

  // Inline AI Enhancement Apply Changes Handler
  const handleApplyAIChanges = async (enhancedContent, sectionType, sectionIndex, enhancementData) => {
    try {
      const updatedData = { ...resumeData };
      
      if (sectionType === 'summary') {
        updatedData.parsedData.summary = enhancedContent;
      } else if (sectionType === 'experience' && sectionIndex !== undefined) {
        if (updatedData.parsedData.experience && updatedData.parsedData.experience[sectionIndex]) {
          updatedData.parsedData.experience[sectionIndex].description = enhancedContent;
        }
      } else if (sectionType === 'projects' && sectionIndex !== undefined) {
        if (updatedData.parsedData.projects && updatedData.parsedData.projects[sectionIndex]) {
          updatedData.parsedData.projects[sectionIndex].description = enhancedContent;
        }
      }
      
      // Update local state
      setResumeData(updatedData);
      setHasChanges(true);
      
      // Auto-save changes to database
      const response = await fetch.put(`/resume/${resumeId}`, {
        parsedData: updatedData.parsedData,
      });

      if (response.success) {
        message.success(`${sectionType === 'summary' ? 'Summary' : sectionType === 'experience' ? 'Experience' : 'Project'} enhanced and saved successfully!`);
        // Refresh resume data to ensure consistency
        await fetchResumeData();
      } else {
        throw new Error("Failed to save AI enhancement");
      }
    } catch (error) {
      logger.error("AI Enhancement save error", {
        extras: {
          error: error.message || error,
          context: "ai_enhancement_save_error"
        }
      });
      message.error("AI enhancement applied but failed to save. Please refresh and try again.");
    }
  };

  // AI Enhancement Handlers
  const openAIEnhanceModal = (sectionType, data, index = null) => {
    setAiEnhanceType(sectionType);
    setAiEnhanceIndex(index);
    setAiEnhanceData(null);
    
    // Pre-fill the form with current data for context
    let currentContent = '';
    if (sectionType === 'summary') {
      currentContent = typeof data === 'string' ? data : data?.content || '';
    } else if (sectionType === 'experience') {
      // Show context but focus on description
      currentContent = `Job Description:\n${data?.description || ''}`;
    } else if (sectionType === 'education') {
      currentContent = `${data?.degree || ''} from ${data?.school || ''}\n${data?.description || ''}`;
    } else if (sectionType === 'projects') {
      // Show description and technologies
      const techStr = Array.isArray(data?.technologies) ? data.technologies.join(', ') : (data?.technologies || '');
      currentContent = `Project Description:\n${data?.description || ''}\n\nTechnologies:\n${techStr}`;
    }
    
    aiEnhanceForm.setFieldsValue({
      userRequest: '',
      currentContent: currentContent,
    });
    
    setAiEnhanceModal(true);
  };

  const handleAIEnhance = async () => {
    try {
      setAiEnhanceLoading(true);
      const values = await aiEnhanceForm.validateFields();
      
      // Get current section data
      let currentData = {};
      if (aiEnhanceType === 'summary') {
        const summary = resumeData.parsedData.summary || '';
        currentData = typeof summary === 'string' ? { content: summary } : summary;
      } else if (aiEnhanceType === 'experience') {
        currentData = resumeData.parsedData.experience?.[aiEnhanceIndex] || {};
      } else if (aiEnhanceType === 'education') {
        currentData = resumeData.parsedData.education?.[aiEnhanceIndex] || {};
      } else if (aiEnhanceType === 'projects') {
        currentData = resumeData.parsedData.projects?.[aiEnhanceIndex] || {};
      }

      // Call AI enhancement API
      const response = await fetch.post('/resume/ai-enhance', {
        resumeId,
        sectionType: aiEnhanceType,
        sectionIndex: aiEnhanceIndex,
        userRequest: values.userRequest,
        currentData: currentData
      });

      if (response.data) {
        setAiEnhanceData(response.data);
        message.success('AI enhancement generated successfully!');
      } else {
        message.error('Failed to generate AI enhancement');
      }
    } catch (error) {
      logger.error('AI enhancement error', {
        extras: {
          error: error.message || error,
          context: "ai_enhancement_generation_error"
        }
      });
      message.error('Error generating AI enhancement: ' + (error.response?.data?.message || error.message));
    } finally {
      setAiEnhanceLoading(false);
      setEnhancingSection(null);
    }
  };

  const applyAIEnhancement = async () => {
    if (!aiEnhanceData?.enhancementId) {
      message.error('No enhancement data to apply');
      return;
    }

    try {
      setAiEnhanceLoading(true);
      
      // Apply the enhancement to the resume
      const response = await fetch.post('/resume/apply-enhancement', {
        enhancementId: aiEnhanceData.enhancementId,
        resumeId
      });

      if (response.data) {
        // Update the local resume data
        const updatedData = { ...resumeData };
        const enhancedContent = response.data.appliedEnhancement;
        
        if (aiEnhanceType === 'summary') {
          updatedData.parsedData.summary = enhancedContent.content || enhancedContent;
        } else if (aiEnhanceType === 'experience' && aiEnhanceIndex !== null) {
          // For experience, enhancedContent is just the description string
          updatedData.parsedData.experience[aiEnhanceIndex] = {
            ...updatedData.parsedData.experience[aiEnhanceIndex],
            description: enhancedContent
          };
        } else if (aiEnhanceType === 'education' && aiEnhanceIndex !== null) {
          updatedData.parsedData.education[aiEnhanceIndex] = {
            ...updatedData.parsedData.education[aiEnhanceIndex],
            ...enhancedContent
          };
        } else if (aiEnhanceType === 'projects' && aiEnhanceIndex !== null) {
          // For projects, enhancedContent contains description and technologies
          updatedData.parsedData.projects[aiEnhanceIndex] = {
            ...updatedData.parsedData.projects[aiEnhanceIndex],
            description: enhancedContent.description,
            technologies: enhancedContent.technologies || updatedData.parsedData.projects[aiEnhanceIndex].technologies
          };
        }

        setResumeData(updatedData);
        setHasChanges(true);
        setAiEnhanceModal(false);
        setAiEnhanceData(null);
        message.success(`${aiEnhanceType.charAt(0).toUpperCase() + aiEnhanceType.slice(1)} enhanced successfully!`);
      } else {
        message.error('Failed to apply AI enhancement');
      }
    } catch (error) {
      logger.error('Apply enhancement error', {
        extras: {
          error: error.message || error,
          context: "apply_enhancement_error"
        }
      });
      message.error('Error applying enhancement: ' + (error.response?.data?.message || error.message));
    } finally {
      setAiEnhanceLoading(false);
      setEnhancingSection(null);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <Layout>
          <Content style={{ padding: "24px" }}>
            <Title level={2}>Modal-Based Resume Editor</Title>
            <Card>
              <Skeleton active paragraph={{ rows: 10 }} />
            </Card>
          </Content>
        </Layout>
      </AppLayout>
    );
  }

  if (error || !resumeData) {
    return (
      <AppLayout>
        <Layout>
          <Content style={{ padding: "24px" }}>
            <Title level={2}>Modal-Based Resume Editor</Title>
            <Card>
              <Text type="danger">Error loading resume data: {error}</Text>
            </Card>
          </Content>
        </Layout>
      </AppLayout>
    );
  }


  return (
    <AppLayout>
      {/* Navigation Header */}

      {/* <div style={{ 
        background: "#ffffff", 
        borderBottom: "1px solid #e5e7eb",
        padding: "12px 24px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <Title level={4} style={{ margin: 0, color: "#722ed1" }}>
            📝 Modal-Based Editor
          </Title>
          <Badge status="processing" text="Form-based editing" />
        </div>
        
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <Button
            type={hasChanges ? "primary" : "default"}
            icon={<SaveOutlined />}
            onClick={saveChanges}
            disabled={!hasChanges}
          >
            {hasChanges ? "Save Changes" : "Saved"}
          </Button>
          
          <Dropdown
            menu={{
              items: [
                {
                  key: 'choice',
                  label: 'Editing Options',
                  icon: <DownOutlined />,
                  onClick: () => navigate(`/app/resume/${resumeId}/choice`)
                },
                {
                  key: 'chat',
                  label: 'AI Chat Editor',
                  icon: <ExclamationCircleOutlined />,
                  onClick: () => navigate(`/app/resume/${resumeId}/chat`)
                },
                {
                  key: 'preview',
                  label: 'Preview & Export',
                  icon: <EyeOutlined />,
                  onClick: () => navigate(`/app/resume/${resumeId}/preview`)
                }
              ]
            }}
            trigger={['click']}
          >
            <Button icon={<DownOutlined />}>
              Switch Editor
            </Button>
          </Dropdown>
          
          <Button 
            type="primary"
            icon={<EyeOutlined />}
            onClick={handlePreviewResume}
          >
            Preview & Export
          </Button>
        </div>
      </div> */}

      {/* Full Width Resume Preview with Inline Controls */}
      <div
        style={{
          height: "100vh", // Full viewport height
          width: "100%",
          backgroundColor: "#ffffff",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Resume Preview Panel - Full Width with Inline Edit Controls */}
        <ResumePreviewPanelWithControls
          resumeData={resumeData}
          setResumeData={setResumeData}
          onPreview={handlePreviewResume}
          // Credit-checked edit handlers - check credits before opening modals
          onEditPersonalInfo={withCreditCheck(openPersonalInfoModal)}
          onEditSummary={withCreditCheck(openSummaryModal)}
          onAIEnhanceSummary={openAIEnhanceModal}
          getDynamicPlacement={getDynamicPlacement}
          getTooltipOverlayStyle={getTooltipOverlayStyle}
          onAddExperience={withCreditCheck(() => openExperienceModal(), true)}
          onEditExperience={withCreditCheck(openExperienceModal)}
          onDeleteExperience={deleteExperience}
          onAIEnhanceExperience={openAIEnhanceModal}
          onAddEducation={withCreditCheck(() => openEducationModal(), true)}
          onEditEducation={withCreditCheck(openEducationModal)}
          onDeleteEducation={deleteEducation}
          onAIEnhanceEducation={openAIEnhanceModal}
          onEditSkills={withCreditCheck(openSkillsModal)}
          onAddProject={withCreditCheck(() => openProjectModal(), true)}
          onEditProject={withCreditCheck(openProjectModal)}
          onDeleteProject={deleteProject}
          onAIEnhanceProject={openAIEnhanceModal}
          onAddCertification={withCreditCheck(() => openCertificationModal(), true)}
          onEditCertification={withCreditCheck(openCertificationModal)}
          onDeleteCertification={deleteCertification}
          onEditAchievements={withCreditCheck(openAchievementsModal)}
          onEditLanguages={withCreditCheck(openLanguagesModal)}
          onEditAwards={withCreditCheck(openAwardsModal)}
          // New Section props - credit checked
          onEditVolunteering={withCreditCheck(openVolunteeringModal)}
          onEditInterests={withCreditCheck(openInterestsModal)}
          onEditIndustryExpertise={withCreditCheck(openIndustryExpertiseModal)}
          onEditPublications={withCreditCheck(openPublicationsModal)}
          onEditReferences={withCreditCheck(openReferencesModal)}
          // Delete Section prop (for new sections) - no credit needed for delete
          onDeleteSection={deleteSection}
          // Custom Section props - credit checked
          onEditCustomSection={withCreditCheck(openEditCustomSectionModal)}
          onDeleteCustomSection={deleteCustomSection}
          // Add Section props - requires add_section credits
          onAddSection={withCreditCheck(() => setAddSectionModal(true), true)}
          visibleSections={visibleSections}
          // Credit state for UI disabling
          canManualEdit={canManualEdit}
          canAddSection={canAddSection}
          creditBalance={creditBalance}
          manualEditCost={manualEditCost}
          addSectionCost={addSectionCost}
          // Reorder items within sections (drag and drop)
          onReorderItems={handleReorderItems}
          // Inline AI Enhancement props
          activeInlineInput={activeInlineInput}
          setActiveInlineInput={setActiveInlineInput}
          inlinePrompt={inlinePrompt}
          setInlinePrompt={setInlinePrompt}
          enhancingSection={enhancingSection}
          handleInlineEnhance={handleInlineEnhance}
          // Public Resume props
          isPublic={isPublic}
          onOpenPublicModal={() => setPublicResumeModal(true)}
          // Edit Mode (Resume/Cover Letter)
          editMode={editMode}
          setEditMode={setEditMode}
          // Section Titles props
          sectionTitles={sectionTitles}
          onSectionTitleChange={handleSectionTitleChange}
        />
      </div>

      {/* Floating Save Button */}
      {hasChanges && (
        <FloatButton
          icon={<SaveOutlined />}
          type="primary"
          onClick={saveChanges}
          tooltip="Save Changes"
        />
      )}

      {/* Modals */}
      
      {/* Personal Info Modal */}
      <Modal
        title="Edit Personal Information"
        open={personalInfoModal}
        onCancel={() => setPersonalInfoModal(false)}
        onOk={handlePersonalInfoSave}
        width={600}
      >
        <Form form={personalInfoForm} layout="vertical" requiredMark={false}>
          <Form.Item label={<span>Full Name <span style={{ color: '#ff4d4f' }}>*</span></span>} name="name" rules={[{ required: true, message: 'Please enter your name' }]}>
            <Input placeholder="Enter your full name" />
          </Form.Item>
          <Form.Item label={<span>Email <span style={{ color: '#ff4d4f' }}>*</span></span>} name="email" rules={[{ required: true, type: 'email', message: 'Please enter a valid email' }]}>
            <Input placeholder="Enter your email" />
          </Form.Item>
          <Form.Item label="Phone" name="phone">
            <Input placeholder="Enter your phone number" />
          </Form.Item>
          <Form.Item label="Location" name="location">
            <Input placeholder="Enter your location" />
          </Form.Item>
          <Form.Item label="LinkedIn" name="linkedin">
            <Input placeholder="Enter your LinkedIn profile URL" />
          </Form.Item>
          <Form.Item label="GitHub" name="github">
            <Input placeholder="Enter your GitHub profile URL" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Summary Modal */}
      <Modal
        title="Edit Professional Summary"
        open={summaryModal}
        onCancel={() => {
          setSummaryModal(false);
          setShowSummaryEnhance(false);
          setSummaryEnhancePrompt('');
        }}
        onOk={handleSummarySave}
        width={700}
      >
        <Form form={summaryForm} layout="vertical" requiredMark={false}>
          <Form.Item 
            label={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>Professional Summary</span>
                <Button 
                  size="small"
                  type="primary"
                  style={{
                    marginLeft: '4px',
                    padding: '0 8px',
                    height: '24px',
                    fontSize: '14px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
                    fontWeight: '500',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowSummaryEnhance(!showSummaryEnhance)}
                >
                  Enhance with AI ✨
                </Button>
              </div>
            } 
            name="summary"
          >
            <TextArea 
              rows={6} 
              placeholder="Write a compelling professional summary that highlights your key qualifications and career objectives..." 
            />
          </Form.Item>
          
          {/* AI Enhancement inline for Summary - Show/Hide */}
          {showSummaryEnhance && (
            <div style={{ 
              margin: '8px 0', 
              padding: '12px', 
              backgroundColor: '#f0f8ff', 
              borderRadius: '6px', 
              border: '1px solid #d1ecf1'
            }}>
              <Input.TextArea
                placeholder="Describe what you want to improve in your summary..."
                value={summaryEnhancePrompt || ''}
                onChange={(e) => setSummaryEnhancePrompt(e.target.value)}
                rows={2}
                style={{ marginBottom: '8px' }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button 
                  type="primary" 
                  size="small"
                  loading={enhancingSection === 'summary'}
                  onClick={() => handleFormEnhance('summary', 'summary', null)}
                  style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                >
                  Enhance With AI
                </Button>
                <Button 
                  size="small"
                  onClick={() => {
                    setSummaryEnhancePrompt('');
                    setShowSummaryEnhance(false);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Form>
      </Modal>

      {/* Experience Modal */}
      <Modal
        title={editingExperience === -1 ? "Add Work Experience" : "Edit Work Experience"}
        open={experienceModal}
        onCancel={() => {
          setExperienceModal(false);
          setIsCurrentJob(false); // Reset state when modal is cancelled
          setShowExperienceEnhance(false);
          setExperienceEnhancePrompt('');
          setTempExperienceAIAnalysis(null); // Clear temporary AI analysis
        }}
        onOk={handleExperienceSave}
        width={700}
      >
        <Form form={experienceForm} layout="vertical" requiredMark={false}>
          <Form.Item label={<span>Job Title <span style={{ color: '#ff4d4f' }}>*</span></span>} name="title" rules={[{ required: true, message: 'Please enter the job title' }]}>
            <Input placeholder="e.g., Software Engineer" />
          </Form.Item>
          <Form.Item label={<span>Company <span style={{ color: '#ff4d4f' }}>*</span></span>} name="company" rules={[{ required: true, message: 'Please enter the company name' }]}>
            <Input placeholder="e.g., Google Inc." />
          </Form.Item>
          <Form.Item label="Location" name="location">
            <Input placeholder="e.g., New York, NY (Optional)" />
          </Form.Item>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item label="Start Date" name="startDate" style={{ flex: 1 }}>
              <DatePicker 
                picker="month" 
                placeholder="Select start date" 
                style={{ width: '100%' }}
                disabledDate={(current) => {
                  const endDate = experienceForm.getFieldValue('endDate');
                  if (!endDate || isCurrentJob) return false;
                  // Disable dates after the end date
                  return current && current.isAfter(dayjs(endDate).endOf('month'));
                }}
              />
            </Form.Item>
            <Form.Item label="End Date" name="endDate" style={{ flex: 1 }}>
              <DatePicker 
                picker="month" 
                placeholder="Select end date" 
                style={{ width: '100%' }}
                disabled={isCurrentJob}
                disabledDate={(current) => {
                  const startDate = experienceForm.getFieldValue('startDate');
                  if (!startDate) return false;
                  // Disable dates before the start date
                  return current && current.isBefore(dayjs(startDate).startOf('month'));
                }}
              />
            </Form.Item>
          </div>
          <Form.Item name="current" valuePropName="checked">
            <Checkbox 
              onChange={(e) => {
                setIsCurrentJob(e.target.checked);
                if (e.target.checked) {
                  experienceForm.setFieldValue('endDate', null);
                }
              }}
            >
              I currently work here
            </Checkbox>
          </Form.Item>
          
          {/* AI Enhancement inline for Experience - Show/Hide */}
          {showExperienceEnhance && (
            <div style={{ 
              margin: '8px 0', 
              padding: '12px', 
              backgroundColor: '#f0f8ff', 
              borderRadius: '6px', 
              border: '1px solid #d1ecf1'
            }}>
              <Input.TextArea
                placeholder="Describe how to improve this job description..."
                value={experienceEnhancePrompt || ''}
                onChange={(e) => setExperienceEnhancePrompt(e.target.value)}
                rows={2}
                style={{ marginBottom: '8px' }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button 
                  type="primary" 
                  size="small"
                  loading={enhancingSection === 'experience'}
                  onClick={() => handleFormEnhance('experience', 'description', editingExperience)}
                >
                  Enhance With AI
                </Button>
                <Button 
                  size="small"
                  onClick={() => {
                    setShowExperienceEnhance(false);
                    setExperienceEnhancePrompt('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          <Form.Item 
            label={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>Job Description</span>
                <Button 
                  size="small"
                  type="primary"
                  style={{
                    marginLeft: '4px',
                    padding: '0 8px',
                    height: '24px',
                    fontSize: '14px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
                    fontWeight: '500',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowExperienceEnhance(!showExperienceEnhance)}
                >
                  Enhance with AI ✨
                </Button>
              </div>
            } 
            name="description"
          >
            <TextArea 
              rows={6} 
              placeholder="Describe your key responsibilities and achievements in this role..." 
              style={{ 
                minHeight: '150px',
                whiteSpace: 'pre-wrap',
                lineHeight: '1.8',
                fontFamily: 'Roboto'
              }}
              className="resume-description-textarea"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Education Modal */}
      <Modal
        title={editingEducation === -1 ? "Add Education" : "Edit Education"}
        open={educationModal}
        onCancel={() => setEducationModal(false)}
        onOk={handleEducationSave}
        width={600}
      >
        <Form form={educationForm} layout="vertical" requiredMark={false}>
          <Form.Item label={<span>Degree <span style={{ color: '#ff4d4f' }}>*</span></span>} name="degree" rules={[{ required: true, message: 'Please enter your degree' }]}>
            <Input placeholder="e.g., Bachelor of Science in Computer Science" />
          </Form.Item>
          <Form.Item label={<span>School/University <span style={{ color: '#ff4d4f' }}>*</span></span>} name="school" rules={[{ required: true, message: 'Please enter the school name' }]}>
            <Input placeholder="e.g., Stanford University" />
          </Form.Item>
          
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item label="Start Date" name="startDate" style={{ flex: 1 }}>
              <DatePicker 
                picker="month" 
                placeholder="Select start date" 
                style={{ width: '100%' }}
                disabledDate={(current) => {
                  const endDate = educationForm.getFieldValue('endDate');
                  if (!endDate) return false;
                  // Disable dates after the end date
                  return current && current.isAfter(dayjs(endDate).endOf('month'));
                }}
              />
            </Form.Item>
            <Form.Item label="End Date" name="endDate" style={{ flex: 1 }}>
              <DatePicker 
                picker="month" 
                placeholder="Select end date" 
                style={{ width: '100%' }}
                disabledDate={(current) => {
                  const startDate = educationForm.getFieldValue('startDate');
                  if (!startDate) return false;
                  // Disable dates before the start date
                  return current && current.isBefore(dayjs(startDate).startOf('month'));
                }}
              />
            </Form.Item>
          </div>

          <Form.Item label="Location (Optional)" name="location">
            <Input placeholder="e.g., Stanford, CA" />
          </Form.Item>
          <Form.Item label="GPA (Optional)" name="gpa">
            <Input placeholder="e.g., 3.8/4.0" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Skills Modal */}
      <Modal
        title={resumeData?.parsedData?.skills?.categorized ? "Edit Categorized Skills" : "Edit Skills"}
        open={skillsModal}
        onCancel={() => setSkillsModal(false)}
        onOk={handleSkillsSave}
        width={600}
      >
        {resumeData?.parsedData?.skills?.categorized ? (
          // Categorized Skills Interface - Compact
          <div>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Select
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  style={{ width: 100 }}
                  size="small"
                >
                  <Option value="technical">Technical</Option>
                  <Option value="soft">Soft</Option>
                  <Option value="tools">Tools</Option>
                  <Option value="other">Other</Option>
                </Select>
                <Input
                  placeholder="Enter skill..."
                  value={categoryInput}
                  onChange={(e) => setCategoryInput(e.target.value)}
                  onPressEnter={addCategorizedSkill}
                  style={{ flex: 1 }}
                  size="small"
                />
                <Button
                  type="primary"
                  onClick={addCategorizedSkill}
                  disabled={!categoryInput.trim()}
                  size="small"
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Compact categorized skills display */}
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {Object.entries(tempCategorizedSkills).map(([category, skills]) => (
                <div key={category} style={{ marginBottom: '12px' }}>
                  <Text strong style={{ fontSize: '12px', marginBottom: '4px', display: 'block' }}>
                    {category === 'technical' ? 'Technical' : category === 'soft' ? 'Soft' : category === 'tools' ? 'Tools' : 'Other'} ({skills.length})
                  </Text>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    minHeight: '28px',
                    border: '1px dashed #d9d9d9',
                    padding: '6px',
                    borderRadius: '4px'
                  }}>
                    {skills.map((skill, index) => (
                      <Tag
                        key={index}
                        closable
                        onClose={() => removeCategorizedSkill(category, skill)}
                        size="small"
                        style={{ margin: '1px' }}
                      >
                        {skill}
                      </Tag>
                    ))}
                    {skills.length === 0 && (
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        No {category} skills
                      </Text>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Flat Skills List Interface
          <div>
            <div style={{ marginBottom: '16px' }}>
              <Input
                placeholder="Enter a skill and press Enter"
                value={skillInput}
                onChange={(e) => setSkillInput(e.target.value)}
                onPressEnter={addSkill}
                suffix={
                  <Button type="link" onClick={addSkill} disabled={!skillInput.trim()}>
                    Add
                  </Button>
                }
              />
            </div>

            <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>Your Skills:</Text>
              {tempSkills.length > 0 && (
                <Button
                  type="link"
                  danger
                  size="small"
                  onClick={clearAllSkills}
                  style={{ padding: '0' }}
                >
                  Clear All
                </Button>
              )}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', minHeight: '100px', border: '1px dashed #d9d9d9', padding: '12px', borderRadius: '6px', alignItems: 'flex-start' }}>
              {tempSkills.map((skill, index) => (
                <Tag
                  key={index}
                  closable
                  onClose={() => removeSkill(skill)}
                  style={{ marginBottom: '4px' }}
                >
                  {skill}
                </Tag>
              ))}

              {tempSkills.length === 0 && (
                <Text type="secondary" style={{ width: '100%', textAlign: 'center', padding: '20px' }}>
                  No skills added yet. Use the input above to add skills.
                </Text>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Project Modal */}
      <Modal
        title={editingProject === -1 ? "Add Project" : "Edit Project"}
        open={projectModal}
        onCancel={() => {
          setProjectModal(false);
          setShowProjectEnhance(false);
          setProjectEnhancePrompt('');
          setTempProjectAIAnalysis(null); // Clear temporary AI analysis
        }}
        onOk={handleProjectSave}
        width={700}
      >
        <Form form={projectForm} layout="vertical" requiredMark={false}>
          <Form.Item label={<span>Project Name <span style={{ color: '#ff4d4f' }}>*</span></span>} name="name" rules={[{ required: true, message: 'Please enter the project name' }]}>
            <Input placeholder="e.g., E-commerce Website" />
          </Form.Item>
          
          {/* AI Enhancement inline for Projects - Show/Hide */}
          {showProjectEnhance && (
            <div style={{ 
              margin: '8px 0', 
              padding: '12px', 
              backgroundColor: '#f0f8ff', 
              borderRadius: '6px', 
              border: '1px solid #d1ecf1'
            }}>
              <Input.TextArea
                placeholder="Describe how to improve this project description..."
                value={projectEnhancePrompt || ''}
                onChange={(e) => setProjectEnhancePrompt(e.target.value)}
                rows={2}
                style={{ marginBottom: '8px' }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button 
                  type="primary" 
                  size="small"
                  loading={enhancingSection === 'projects'}
                  onClick={() => handleFormEnhance('projects', 'description', editingProject)}
                >
                  Enhance With AI
                </Button>
                <Button 
                  size="small"
                  onClick={() => {
                    setShowProjectEnhance(false);
                    setProjectEnhancePrompt('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          
          <Form.Item 
            label={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>Description</span>
                <Button 
                  size="small"
                  type="primary"
                  style={{
                    marginLeft: '4px',
                    padding: '0 8px',
                    height: '24px',
                    fontSize: '14px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4), 0 0 20px rgba(16, 185, 129, 0.1)',
                    fontWeight: '500',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowProjectEnhance(!showProjectEnhance)}
                >
                  Enhance with AI ✨
                </Button>
              </div>
            } 
            name="description"
          >
            <TextArea 
              rows={4} 
              placeholder="Describe the project, your role, and key achievements..." 
              style={{ 
                whiteSpace: 'pre-wrap',
                lineHeight: '1.8',
                fontFamily: 'Roboto'
              }}
              className="resume-description-textarea"
            />
          </Form.Item>
          <Form.Item label="Technologies Used" name="technologies">
            <Input placeholder="e.g., React, Node.js, MongoDB (comma-separated)" />
          </Form.Item>
          <Form.Item label="Project Link (Optional)" name="link">
            <Input placeholder="e.g., https://github.com/yourname/project" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Certification Modal */}
      <Modal
        title={editingCertification === -1 ? "Add Certification" : "Edit Certification"}
        open={certificationModal}
        onCancel={() => setCertificationModal(false)}
        onOk={handleCertificationSave}
        width={600}
      >
        <Form form={certificationForm} layout="vertical" requiredMark={false}>
          <Form.Item label={<span>Certification Name <span style={{ color: '#ff4d4f' }}>*</span></span>} name="name" rules={[{ required: true, message: 'Please enter the certification name' }]}>
            <Input placeholder="e.g., AWS Certified Solutions Architect" />
          </Form.Item>
          <Form.Item label={<span>Issuing Organization <span style={{ color: '#ff4d4f' }}>*</span></span>} name="issuer" rules={[{ required: true, message: 'Please enter the issuing organization' }]}>
            <Input placeholder="e.g., Amazon Web Services" />
          </Form.Item>
          <Form.Item label="Date Obtained" name="date">
            <DatePicker picker="month" placeholder="Select date" style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Achievements Modal */}
      <Modal
        title="Edit Achievements"
        open={achievementsModal}
        onCancel={() => setAchievementsModal(false)}
        onOk={handleAchievementsSave}
        width={700}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Your Achievements:</Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={addAchievement}
            >
              Add Achievement
            </Button>
          </div>

          {tempAchievements.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#999',
              border: '1px dashed #d9d9d9',
              borderRadius: '6px'
            }}>
              <Text type="secondary">No achievements added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Achievement" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempAchievements);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempAchievements(reordered);
            }}>
              <Droppable droppableId="achievements-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {tempAchievements.map((achievement, index) => (
                      <Draggable
                        key={`achievement-modal-${index}`}
                        draggableId={`achievement-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Achievement #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  danger
                                  onClick={() => removeAchievement(index)}
                                  style={{ border: 'none', boxShadow: 'none' }}
                                />
                              </div>
                            </div>
                            <TextArea
                              placeholder="Describe your achievement using bullet points. Each line will be formatted as a bullet point automatically."
                              value={achievement.description}
                              onChange={(e) => updateAchievement(index, 'description', e.target.value)}
                              rows={4}
                              style={{
                                backgroundColor: '#fff',
                                whiteSpace: 'pre-wrap',
                                lineHeight: '1.6',
                                fontFamily: 'Roboto'
                              }}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </Modal>

      {/* Languages Modal */}
      <Modal
        title="Edit Languages"
        open={languagesModal}
        onCancel={() => setLanguagesModal(false)}
        onOk={handleLanguagesSave}
        width={600}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Your Languages:</Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={addLanguage}
            >
              Add Language
            </Button>
          </div>

          {tempLanguages.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#999',
              border: '1px dashed #d9d9d9',
              borderRadius: '6px'
            }}>
              <Text type="secondary">No languages added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Language" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempLanguages);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempLanguages(reordered);
            }}>
              <Droppable droppableId="languages-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {tempLanguages.map((language, index) => (
                      <Draggable
                        key={`lang-modal-${index}`}
                        draggableId={`lang-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Language #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  danger
                                  onClick={() => removeLanguage(index)}
                                  style={{ border: 'none', boxShadow: 'none' }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '12px' }}>
                              <Input
                                placeholder="Language (e.g., English, Spanish)"
                                value={language.name}
                                onChange={(e) => updateLanguage(index, 'name', e.target.value)}
                                style={{ flex: 1 }}
                              />
                              <Select
                                placeholder="Proficiency"
                                value={language.proficiency || undefined}
                                onChange={(value) => updateLanguage(index, 'proficiency', value)}
                                style={{ width: '150px' }}
                              >
                                <Select.Option value="Native">Native</Select.Option>
                                <Select.Option value="Fluent">Fluent</Select.Option>
                                <Select.Option value="Advanced">Advanced</Select.Option>
                                <Select.Option value="Intermediate">Intermediate</Select.Option>
                                <Select.Option value="Basic">Basic</Select.Option>
                              </Select>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </Modal>

      {/* Awards Modal */}
      <Modal
        title="Edit Awards"
        open={awardsModal}
        onCancel={() => setAwardsModal(false)}
        onOk={handleAwardsSave}
        width={700}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Your Awards:</Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={addAward}
            >
              Add Award
            </Button>
          </div>

          {tempAwards.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#999',
              border: '1px dashed #d9d9d9',
              borderRadius: '6px'
            }}>
              <Text type="secondary">No awards added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Award" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempAwards);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempAwards(reordered);
            }}>
              <Droppable droppableId="awards-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {tempAwards.map((award, index) => (
                      <Draggable
                        key={`award-modal-${index}`}
                        draggableId={`award-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Award #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  danger
                                  onClick={() => removeAward(index)}
                                  style={{ border: 'none', boxShadow: 'none' }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Input
                                placeholder="Award Title (e.g., Best Employee Award)"
                                value={award.title}
                                onChange={(e) => updateAward(index, 'title', e.target.value)}
                              />
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <Input
                                  placeholder="Issuing Organization"
                                  value={award.issuer}
                                  onChange={(e) => updateAward(index, 'issuer', e.target.value)}
                                  style={{ flex: 1 }}
                                />
                                <DatePicker
                                  picker="month"
                                  placeholder="Select Date"
                                  value={award.date ? dayjs(award.date, ['MMM YYYY', 'MMMM YYYY', 'YYYY-MM', 'MM/YYYY', 'YYYY']) : null}
                                  onChange={(date) => updateAward(index, 'date', date ? date.format('MMM YYYY') : '')}
                                  style={{ width: '150px' }}
                                />
                              </div>
                              <TextArea
                                placeholder="Description (optional)"
                                value={award.description}
                                onChange={(e) => updateAward(index, 'description', e.target.value)}
                                rows={2}
                              />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </Modal>

      {/* Volunteering Modal */}
      <Modal
        title="Edit Volunteering"
        open={volunteeringModal}
        onCancel={() => setVolunteeringModal(false)}
        onOk={handleVolunteeringSave}
        width={700}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Your Volunteer Experience:</Text>
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={addVolunteering}>
              Add Volunteering
            </Button>
          </div>
          {tempVolunteering.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
              <Text type="secondary">No volunteering experience added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Volunteering" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempVolunteering);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempVolunteering(reordered);
            }}>
              <Droppable droppableId="volunteering-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {tempVolunteering.map((vol, index) => (
                      <Draggable
                        key={`vol-modal-${index}`}
                        draggableId={`vol-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Volunteering #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button icon={<DeleteOutlined />} size="small" danger onClick={() => removeVolunteering(index)} style={{ border: 'none', boxShadow: 'none' }} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Input placeholder="Organization" value={vol.organization} onChange={(e) => updateVolunteering(index, 'organization', e.target.value)} />
                              <Input placeholder="Role/Position" value={vol.role} onChange={(e) => updateVolunteering(index, 'role', e.target.value)} />
                              <Input placeholder="Location" value={vol.location} onChange={(e) => updateVolunteering(index, 'location', e.target.value)} />
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <DatePicker
                                  picker="month"
                                  placeholder="Start Date"
                                  value={vol.startDate ? dayjs(vol.startDate, ['MMM YYYY', 'MMMM YYYY', 'YYYY-MM', 'MM/YYYY', 'YYYY']) : null}
                                  onChange={(date) => updateVolunteering(index, 'startDate', date ? date.format('MMM YYYY') : '')}
                                  style={{ flex: 1 }}
                                />
                                <DatePicker
                                  picker="month"
                                  placeholder="End Date"
                                  value={vol.endDate ? dayjs(vol.endDate, ['MMM YYYY', 'MMMM YYYY', 'YYYY-MM', 'MM/YYYY', 'YYYY']) : null}
                                  onChange={(date) => updateVolunteering(index, 'endDate', date ? date.format('MMM YYYY') : '')}
                                  style={{ flex: 1 }}
                                  disabled={vol.isCurrentRole}
                                />
                              </div>
                              <Checkbox checked={vol.isCurrentRole} onChange={(e) => updateVolunteering(index, 'isCurrentRole', e.target.checked)}>Currently volunteering here</Checkbox>
                              <TextArea placeholder="Description" value={vol.description} onChange={(e) => updateVolunteering(index, 'description', e.target.value)} rows={3} />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </Modal>

      {/* Interests Modal */}
      <Modal
        title="Edit Interests"
        open={interestsModal}
        onCancel={() => setInterestsModal(false)}
        onOk={handleInterestsSave}
        width={600}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Your Interests & Hobbies:</Text>
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={addInterest}>
              Add Interest
            </Button>
          </div>
          {tempInterests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
              <Text type="secondary">No interests added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Interest" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempInterests);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempInterests(reordered);
            }}>
              <Droppable droppableId="interests-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {tempInterests.map((interest, index) => (
                      <Draggable
                        key={`interest-modal-${index}`}
                        draggableId={`interest-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Interest #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button icon={<DeleteOutlined />} size="small" danger onClick={() => removeInterest(index)} style={{ border: 'none', boxShadow: 'none' }} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Input placeholder="Interest (e.g., Photography, Hiking)" value={interest.name} onChange={(e) => updateInterest(index, 'name', e.target.value)} />
                              <TextArea placeholder="Description (optional)" value={interest.description} onChange={(e) => updateInterest(index, 'description', e.target.value)} rows={2} />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </Modal>

      {/* Industry Expertise Modal */}
      <Modal
        title="Edit Industry Expertise"
        open={industryExpertiseModal}
        onCancel={() => setIndustryExpertiseModal(false)}
        onOk={handleIndustryExpertiseSave}
        width={600}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Your Areas of Expertise:</Text>
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={addIndustryExpertise}>
              Add Expertise
            </Button>
          </div>
          {tempIndustryExpertise.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
              <Text type="secondary">No expertise added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Expertise" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempIndustryExpertise);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempIndustryExpertise(reordered);
            }}>
              <Droppable droppableId="industry-expertise-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {tempIndustryExpertise.map((exp, index) => (
                      <Draggable
                        key={`expertise-modal-${index}`}
                        draggableId={`expertise-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Expertise #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button icon={<DeleteOutlined />} size="small" danger onClick={() => removeIndustryExpertise(index)} style={{ border: 'none', boxShadow: 'none' }} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Input placeholder="Expertise Area (e.g., Fintech, Healthcare, E-commerce)" value={exp.name} onChange={(e) => updateIndustryExpertise(index, 'name', e.target.value)} />
                              <Select
                                placeholder="Proficiency Level"
                                value={exp.proficiency || undefined}
                                onChange={(value) => updateIndustryExpertise(index, 'proficiency', value)}
                                style={{ width: '100%' }}
                              >
                                <Select.Option value="Expert">Expert</Select.Option>
                                <Select.Option value="Advanced">Advanced</Select.Option>
                                <Select.Option value="Intermediate">Intermediate</Select.Option>
                                <Select.Option value="Beginner">Beginner</Select.Option>
                              </Select>
                              <Input placeholder="Years of Experience (e.g., 5+ years)" value={exp.yearsOfExperience} onChange={(e) => updateIndustryExpertise(index, 'yearsOfExperience', e.target.value)} />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </Modal>

      {/* Publications Modal */}
      <Modal
        title="Edit Publications"
        open={publicationsModal}
        onCancel={() => setPublicationsModal(false)}
        onOk={handlePublicationsSave}
        width={700}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Your Publications:</Text>
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={addPublication}>
              Add Publication
            </Button>
          </div>
          {tempPublications.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
              <Text type="secondary">No publications added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Publication" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempPublications);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempPublications(reordered);
            }}>
              <Droppable droppableId="publications-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {tempPublications.map((pub, index) => (
                      <Draggable
                        key={`pub-modal-${index}`}
                        draggableId={`pub-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Publication #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button icon={<DeleteOutlined />} size="small" danger onClick={() => removePublication(index)} style={{ border: 'none', boxShadow: 'none' }} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Input placeholder="Publication Title" value={pub.title} onChange={(e) => updatePublication(index, 'title', e.target.value)} />
                              <Input placeholder="Authors (comma separated)" value={Array.isArray(pub.authors) ? pub.authors.join(', ') : pub.authors} onChange={(e) => updatePublication(index, 'authors', e.target.value.split(',').map(a => a.trim()))} />
                              <Input placeholder="Journal/Publication Name" value={pub.journal} onChange={(e) => updatePublication(index, 'journal', e.target.value)} />
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <DatePicker
                                  picker="month"
                                  placeholder="Publication Date"
                                  value={pub.date ? dayjs(pub.date, ['MMM YYYY', 'MMMM YYYY', 'YYYY-MM', 'MM/YYYY', 'YYYY']) : null}
                                  onChange={(date) => updatePublication(index, 'date', date ? date.format('MMM YYYY') : '')}
                                  style={{ flex: 1 }}
                                />
                                <Input placeholder="Link/URL (optional)" value={pub.link} onChange={(e) => updatePublication(index, 'link', e.target.value)} style={{ flex: 1 }} />
                              </div>
                              <TextArea placeholder="Description (optional)" value={pub.description} onChange={(e) => updatePublication(index, 'description', e.target.value)} rows={2} />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </Modal>

      {/* References Modal */}
      <Modal
        title="Edit References"
        open={referencesModal}
        onCancel={() => setReferencesModal(false)}
        onOk={handleReferencesSave}
        width={700}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Your References:</Text>
            <Button type="primary" icon={<PlusOutlined />} size="small" onClick={addReference}>
              Add Reference
            </Button>
          </div>
          {tempReferences.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999', border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
              <Text type="secondary">No references added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Reference" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempReferences);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempReferences(reordered);
            }}>
              <Droppable droppableId="references-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    {tempReferences.map((ref, index) => (
                      <Draggable
                        key={`ref-modal-${index}`}
                        draggableId={`ref-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Reference #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button icon={<DeleteOutlined />} size="small" danger onClick={() => removeReference(index)} style={{ border: 'none', boxShadow: 'none' }} />
                              </div>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <Input placeholder="Full Name" value={ref.name} onChange={(e) => updateReference(index, 'name', e.target.value)} />
                              <Input placeholder="Job Title" value={ref.title} onChange={(e) => updateReference(index, 'title', e.target.value)} />
                              <Input placeholder="Company/Organization" value={ref.company} onChange={(e) => updateReference(index, 'company', e.target.value)} />
                              <div style={{ display: 'flex', gap: '12px' }}>
                                <Input placeholder="Email" value={ref.email} onChange={(e) => updateReference(index, 'email', e.target.value)} style={{ flex: 1 }} />
                                <Input
                                  placeholder="Phone (10 digits)"
                                  value={ref.phone}
                                  onChange={(e) => {
                                    // Only allow digits and limit to 10 characters
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    updateReference(index, 'phone', value);
                                  }}
                                  maxLength={10}
                                  style={{ flex: 1 }}
                                  status={ref.phone && ref.phone.length > 0 && ref.phone.length < 10 ? 'warning' : undefined}
                                />
                              </div>
                              {ref.phone && ref.phone.length > 0 && ref.phone.length < 10 && (
                                <Text type="warning" style={{ fontSize: '12px' }}>Phone number should be 10 digits</Text>
                              )}
                              <Input placeholder="Relationship (e.g., Former Manager)" value={ref.relationship} onChange={(e) => updateReference(index, 'relationship', e.target.value)} />
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </Modal>

      {/* Custom Section Modal */}
      <Modal
        title={editingCustomSectionId ? "Edit Custom Section" : "Create Custom Section"}
        open={customSectionModal}
        onCancel={() => {
          setCustomSectionModal(false);
          setEditingCustomSectionId(null);
        }}
        onOk={handleCustomSectionUpdate}
        width={700}
        okText={editingCustomSectionId ? "Update Section" : "Save Section"}
        getContainer={false}
      >
        <div style={{ marginBottom: '16px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>Section Title:</Text>
            <Input
              placeholder="Enter section title (e.g., Hobbies, Volunteering, Personal Projects)"
              value={tempCustomSection.title}
              onChange={(e) => updateCustomSectionTitle(e.target.value)}
              size="large"
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <Text strong>Section Items:</Text>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={addCustomSectionItem}
            >
              Add Item
            </Button>
          </div>

          {tempCustomSection.items.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#999',
              border: '1px dashed #d9d9d9',
              borderRadius: '6px'
            }}>
              <Text type="secondary">No items added yet.</Text>
              <br />
              <Text type="secondary">Click "Add Item" to get started.</Text>
            </div>
          ) : (
            <DragDropContext onDragEnd={(result) => {
              if (!result.destination) return;
              if (result.source.index === result.destination.index) return;
              const reordered = Array.from(tempCustomSection.items);
              const [moved] = reordered.splice(result.source.index, 1);
              reordered.splice(result.destination.index, 0, moved);
              setTempCustomSection(prev => ({ ...prev, items: reordered }));
            }}>
              <Droppable droppableId="custom-section-modal-list">
                {(droppableProvided) => (
                  <div
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                    style={{ maxHeight: '350px', overflowY: 'auto' }}
                  >
                    {tempCustomSection.items.map((item, index) => (
                      <Draggable
                        key={`custom-item-modal-${index}`}
                        draggableId={`custom-item-modal-${index}`}
                        index={index}
                      >
                        {(draggableProvided, draggableSnapshot) => (
                          <div
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            style={{
                              ...draggableProvided.draggableProps.style,
                              marginBottom: '12px',
                              padding: '12px',
                              border: '1px solid #f0f0f0',
                              borderRadius: '6px',
                              backgroundColor: draggableSnapshot.isDragging ? '#fff' : '#fafafa',
                              boxShadow: draggableSnapshot.isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                              <Text strong>Item #{index + 1}</Text>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <div
                                  {...draggableProvided.dragHandleProps}
                                  style={{
                                    cursor: 'grab',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                  }}
                                  title="Drag to reorder"
                                >
                                  <SwapOutlined style={{ fontSize: '12px', transform: 'rotate(90deg)' }} />
                                </div>
                                <Button
                                  icon={<DeleteOutlined />}
                                  size="small"
                                  danger
                                  onClick={() => removeCustomSectionItem(index)}
                                  style={{ border: 'none', boxShadow: 'none' }}
                                />
                              </div>
                            </div>
                            <TextArea
                              placeholder="Enter content for this item (e.g., description, achievement, detail)"
                              value={item.content}
                              onChange={(e) => updateCustomSectionItem(index, e.target.value)}
                              rows={3}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}

          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#f6ffed',
            border: '1px solid #b7eb8f',
            borderRadius: '6px'
          }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Tip: Create any section you need - hobbies, volunteer work, personal projects, certifications, or anything else that showcases your unique value.
            </Text>
          </div>
        </div>
      </Modal>

      {/* AI Enhancement Modal */}
      <Modal
        title={`AI Enhance ${aiEnhanceType.charAt(0).toUpperCase() + aiEnhanceType.slice(1)}`}
        open={aiEnhanceModal}
        onCancel={() => {
          setAiEnhanceModal(false);
          setAiEnhanceData(null);
        }}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => {
            setAiEnhanceModal(false);
            setAiEnhanceData(null);
          }}>
            Cancel
          </Button>,
          !aiEnhanceData ? (
            <Button
              key="enhance"
              type="primary"
              loading={aiEnhanceLoading}
              onClick={handleAIEnhance}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              Enhance with AI ✨
            </Button>
          ) : (
            <Button
              key="apply"
              type="primary"
              icon={<ThunderboltOutlined />}
              loading={aiEnhanceLoading}
              onClick={applyAIEnhancement}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              Apply Enhancement
            </Button>
          )
        ]}
      >
        <Form form={aiEnhanceForm} layout="vertical" requiredMark={false}>
          <Form.Item 
            label={<span>What would you like to improve? <span style={{ color: '#ff4d4f' }}>*</span></span>}
            name="userRequest" 
            rules={[{ required: true, message: 'Please describe what you want to enhance' }]}
          >
            <TextArea 
              rows={3} 
              placeholder="e.g., Make it more impactful, add quantifiable achievements, improve keywords for ATS..."
              style={{ resize: 'none' }}
            />
          </Form.Item>

          <Form.Item label="Current Content" name="currentContent">
            <TextArea 
              rows={4} 
              disabled 
              style={{ backgroundColor: '#f5f5f5', color: '#666', resize: 'none' }}
            />
          </Form.Item>

          {aiEnhanceData && (
            <>
              <div style={{ marginBottom: '16px' }}>
                <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                  🤖 AI Enhanced Content:
                </Text>
              </div>

              <div style={{
                padding: '16px',
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                marginBottom: '16px'
              }}>
                {aiEnhanceType === 'summary' ? (
                  <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                    {aiEnhanceData.enhancedContent}
                  </div>
                ) : (
                  <div>
                    {aiEnhanceType === 'experience' && (
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                        {aiEnhanceData.enhancedContent}
                      </div>
                    )}
                    {aiEnhanceType === 'education' && (
                      <>
                        <div style={{ marginBottom: '8px' }}>
                          <Text strong>{aiEnhanceData.enhancedContent.degree}</Text>
                          <Text type="secondary"> from {aiEnhanceData.enhancedContent.school}</Text>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                          {aiEnhanceData.enhancedContent.description}
                        </div>
                      </>
                    )}
                    {aiEnhanceType === 'projects' && (
                      <>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', marginBottom: '12px' }}>
                          <Text strong style={{ display: 'block', marginBottom: '8px' }}>Enhanced Description:</Text>
                          {aiEnhanceData.enhancedContent.description}
                        </div>
                        {aiEnhanceData.enhancedContent.technologies && (
                          <div>
                            <Text strong style={{ display: 'block', marginBottom: '8px' }}>Enhanced Technologies:</Text>
                            <div>
                              {aiEnhanceData.enhancedContent.technologies.map((tech, index) => (
                                <Tag key={index} style={{ marginBottom: '4px' }}>{tech}</Tag>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* {aiEnhanceData.aiAnalysis && (
                <div style={{
                  padding: '12px',
                  backgroundColor: '#f0f7ff',
                  border: '1px solid #d6e4ff',
                  borderRadius: '6px',
                  marginBottom: '16px'
                }}>
                  <div style={{ marginBottom: '8px' }}>
                    <Text strong style={{ color: '#1890ff' }}>
                      💡 AI Analysis:
                    </Text>
                  </div>
                  <div style={{ fontSize: '12px', lineHeight: '1.5' }}>
                    <div><strong>Confidence Score:</strong> {aiEnhanceData.aiAnalysis.confidenceScore}/100</div>
                    {aiEnhanceData.reasoning && (
                      <div style={{ marginTop: '8px' }}>
                        <strong>Enhancement Strategy:</strong><br />
                        {aiEnhanceData.reasoning}
                      </div>
                    )}
                  </div>
                </div>
              )} */}
            </>
          )}
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <DeleteModal />

      <style>{`
        .modal-based-resume .resume-section:hover .section-edit-overlay {
          opacity: 1;
        }

        .modal-based-resume .section-edit-overlay {
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .modal-based-resume .experience-item:hover .item-controls,
        .modal-based-resume .education-item:hover .item-controls {
          opacity: 1 !important;
        }

        .modal-based-resume .experience-item,
        .modal-based-resume .education-item {
          transition: all 0.2s ease;
        }

        .modal-based-resume .experience-item:hover,
        .modal-based-resume .education-item:hover {
          border-color: #1890ff !important;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.1);
        }
      `}</style>

      {/* Public Resume Modal */}
      <PublicResumeModal
        visible={publicResumeModal}
        onClose={() => setPublicResumeModal(false)}
        resumeId={resumeId}
        currentPublicStatus={isPublic}
        onPublicStatusChange={handlePublicStatusChange}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        visible={addSectionModal}
        onClose={() => setAddSectionModal(false)}
        onSelectSection={handleSectionSelect}
        existingSections={visibleSections}
      />

    </AppLayout>
  );
};

export default ModalBasedResume;